import React, { useState, useEffect, useRef } from 'react'
import {
  Form,
  Input,
  Select,
  Row,
  Col,
  Button,
  Tag,
  Upload,
  Space,
  Modal,
  Image,
  Checkbox,
  Carousel,
} from 'antd'

import { PlusOutlined, UploadOutlined, CloseOutlined, DownloadOutlined, CameraOutlined, AimOutlined, ExperimentOutlined, EditOutlined, ScissorOutlined, RotateLeftOutlined, RotateRightOutlined, DeleteOutlined, LeftOutlined, RightOutlined, ThunderboltFilled, ColumnWidthOutlined } from '@ant-design/icons'
import {
  putAsignarMaterialRevisionSTG,
  putLiberarMaterial,
  getReferenciasRepuesto,
  putDesvincularHomologo,
  putRelacionHomologo,
  putDesvincularReemplazante,
  putRelacionReemplazante,
  putDesvincularModelo,
  putUpdateRevision,
  getPlantillaMedidas,
  getPlantillaMedidasOpciones,
  uploadReferenceImage,
  getImagesByMaterial,
  deleteReferenceImage,
  uploadImagenEmpaque,
  getImagenesEmpaque,
  deleteImagenEmpaque,
  uploadImagenCotizacion,
  getImagenCotizacion,
  postCrearEmpaque,
  postCrearProteccion,
  postCrearEmbalaje,
  postCrearMarcaFabrica,
  postGenerarTextoBreve,
  getBasculaPorUsuario,
  basculaWebSocketService,
  postFirmarRevision,
  postGenerarImagenConCotas,
  getGenerarCoordenadas,
  postCotas,
  getImagenCotas,
  deleteImagenCotas,
  postImagenCotasRecortada,
  getImagenArgumentacionCatalogo,
  postImagenArgumentacionCatalogo,
  deleteImagenArgumentacionCatalogo,
} from 'src/services/axiomaService'
import { DESCRIPCION_CORRECTA_MAX_LENGTH, esCampoRequeridoFaltante } from './utils/formUtils'
import './AxiomaFormContainer.scss'
import { toast } from 'react-toastify'
import CompareMaterialsComponent from './CompareMaterialsComponent/CompareMaterialsComponent'
import CompareMotoModelsComponent from './CompareMaterialsComponent/CompareMotoModelsComponent'
import { 
  dibujarFlecha, 
  dibujarCirculoArrastre, 
  dibujarLinea,
  distanciaPuntoALinea, 
  distanciaPuntoACirculo,
  normalizarCoordenadas,
  escalarCoordenadas,
  exportarImagenEcommerce,
  nombreArchivoEcommerce,
} from './utils/canvasUtils'
import { obtenerCoordenadasMedida, extraerLetraLabel, calcularCoordenadasEtiqueta, calcularSiguienteLetraDisponible } from './utils/geometriaUtils'
// Modales
import ModalMedidasCanvas from './Modals/ModalMedidasCanvas'
import ModalImagenCotizacion from './Modals/ModalImagenCotizacion'
import ModalCotasGuardadas from './Modals/ModalCotasGuardadas'
import ModalEdicionImagen from './Modals/ModalEdicionImagen'
import ModalRecorte from './Modals/ModalRecorte'
// Hooks
import { useCanvasDrawing } from './hooks/useCanvasDrawing'
import { useCanvasInteractions } from './hooks/useCanvasInteractions'
import { useJerarquiaCascada } from './hooks/useJerarquiaCascada'
// Constantes
import { CANVAS_COLORS, CANVAS_CONFIG, CURSOR_TYPES, MODO_DIBUJO } from './constants/canvasConstants'
// Secciones del formulario
import DescripcionSection from './FormSections/DescripcionSection'
import CategoriaSection from './FormSections/CategoriaSection'
import MedidasSection from './FormSections/MedidasSection'
import MaterialInfoCard from './FormSections/MaterialInfoCard'
import PhotosCard from './FormSections/PhotosCard'
import ImagenesEmpaqueCard from './FormSections/ImagenesEmpaqueCard'
import BuscarReemplazantesSection from './FormSections/BuscarReemplazantesSection'
import CatalogUpdateCard from './FormSections/CatalogUpdateCard'
import TagCard from './FormSections/TagCard'

const { confirm } = Modal

const { TextArea } = Input

/**
 * Un solo id para Marca fábrica: prioriza revision_marca_fabrica_id; si el backend manda listas
 * legacy, usa el primero; si solo viene revision_marca_fabrica_nombre, resuelve el primer nombre en el catálogo.
 */
const getMarcaFabricaIdFromMaterial = (material, catalogoMarcasFabrica) => {
  if (!material) return null
  const catalogo = Array.isArray(catalogoMarcasFabrica) ? catalogoMarcasFabrica : []

  const firstIdFromArray = (arr) => {
    if (!Array.isArray(arr) || arr.length === 0) return null
    const x = arr[0]
    const id = typeof x === 'object' && x !== null ? x.id : x
    if (id === null || id === undefined || id === '') return null
    return id
  }

  if (material.revision_marca_fabrica_id != null && material.revision_marca_fabrica_id !== '') {
    return material.revision_marca_fabrica_id
  }
  if (Array.isArray(material.revision_marca_fabrica_ids) && material.revision_marca_fabrica_ids.length) {
    const id = firstIdFromArray(material.revision_marca_fabrica_ids)
    if (id != null) return id
  }
  if (Array.isArray(material.revision_marcas_fabrica_ids) && material.revision_marcas_fabrica_ids.length) {
    const id = firstIdFromArray(material.revision_marcas_fabrica_ids)
    if (id != null) return id
  }
  if (Array.isArray(material.revision_marcas_fabrica) && material.revision_marcas_fabrica.length) {
    const id = firstIdFromArray(material.revision_marcas_fabrica)
    if (id != null) return id
  }

  const nombreRaw = material.revision_marca_fabrica_nombre
  if (!nombreRaw || !catalogo.length) return null
  const parte = String(nombreRaw)
    .split(/[,|]/)
    .map((s) => s.trim())
    .find(Boolean)
  if (!parte) return null
  const found = catalogo.find((m) => String(m.nombre ?? '').trim().toUpperCase() === parte.toUpperCase())
  return found ? found.id : null
}

const AxiomaFormContainer = ({
  material,
  onClose,
  onResetData,
  idUser,
  repuestosData,
  marcas,
  segmentos,
  subSegmentos,
  categorias,
  subCategorias,
  isAuditor,
  tiposEmbalaje,
  tiposProteccion,
  tiposEmpaque,
  marcasFabrica,
  modelosAplicacionOrigen,
  onResetAllData,
}) => {
  const [form] = Form.useForm()

  // Cascada por sector (flag HABILITAR_JERARQUIA_CASCADA en useJerarquiaCascada)
  const cascada = useJerarquiaCascada()

  // ==================== ESTADOS: Relaciones y Comparaciones ====================
  const [modelosAplicacionPrincipal, setModelosAplicacionPrincipal] = useState([])
  const [modelosAplicacion, setModelosAplicacion] = useState([])
  const [homologos, setHomologos] = useState([])
  const [reemplazantes, setReemplazantes] = useState([])
  const [categorytoCompare, setCategorytoCompare] = useState(null)
  const [categorytoCompareModelMoto, setCategorytoCompareModelMoto] = useState(null)

  // ==================== ESTADOS: Imágenes y Referencias ====================
  const [images, setImages] = useState([])
  const [imagenesEmpaque, setImagenesEmpaque] = useState([])
  const [imagenCotizacionGuardada, setImagenCotizacionGuardada] = useState(null)
  const [buscarReemplazantesCooldown, setBuscarReemplazantesCooldown] = useState(0)
  const [fotosMaterialListas, setFotosMaterialListas] = useState(false)
  const prevPhotosCountRef = useRef(null)
  // A dónde va la foto de la cámara cuando termina el flujo (recorte, quitar
  // fondo, etc.): fotos del material o imágenes de empaque. Ref porque el
  // listener del socket se registra una sola vez.
  const destinoFotoRef = useRef('material')
  const buscarReemplazantesTimerRef = useRef(null)
  const [catalogUpdateImage, setCatalogUpdateImage] = useState(null)
  const [imagenFirmadaUrl, setImagenFirmadaUrl] = useState(null)
  // Blob de la imagen mostrada en el modal; se pasa directo a Photoroom porque el CSP de prod bloquea fetch a URLs blob:
  const [imagenFirmadaBlob, setImagenFirmadaBlob] = useState(null)
  const [gsUriFirmado, setGsUriFirmado] = useState(null)
  const [gsurlOriginal, setGsurlOriginal] = useState(null)
  const [modalImagenVisible, setModalImagenVisible] = useState(false)
  const [imagenParaEditar, setImagenParaEditar] = useState(null)
  const [imagenCotasUrl, setImagenCotasUrl] = useState(null)
  const [imagenFavorita, setImagenFavorita] = useState(material?.revision_imagen_favorita || null)
  const [requiereActualizacionCatalogo, setRequiereActualizacionCatalogo] = useState(
    material?.revision_requiere_actualizacion_catalogo ?? 0
  )
  // ==================== ESTADOS: Tipos Locales (Embalaje, Empaque, Protección) ====================
  const [nuevoEmbalaje, setNuevoEmbalaje] = useState('')
  const [tiposEmbalajeLocal, setTiposEmbalajeLocal] = useState([])
  const [nuevoEmpaque, setNuevoEmpaque] = useState('')
  const [tiposEmpaqueLocal, setTiposEmpaqueLocal] = useState([])
  const [nuevoProteccion, setNuevoProteccion] = useState('')
  const [tiposProteccionLocal, setTiposProteccionLocal] = useState([])
  const [nuevoMarcaFabrica, setNuevoMarcaFabrica] = useState('')
  const [marcasFabricaLocal, setMarcasFabricaLocal] = useState([])

  // ==================== ESTADOS: Formulario y Validación ====================
  const [camposFaltantes, setCamposFaltantes] = useState([])
  const [loadingTextoBreve, setLoadingTextoBreve] = useState(false)
  const [descripcionBreveIA, setDescripcionBreveIA] = useState(material?.revision_descripcion_breve_ia || '')
  const [descripcionBreveIAModificada, setDescripcionBreveIAModificada] = useState(
    material?.revision_descripcion_breve_ia_modificada !== undefined 
      ? material.revision_descripcion_breve_ia_modificada 
      : 0
  )
  const [descripcionUrgente, setDescripcionUrgente] = useState(
    material?.revision_descripcion_correcta_urgente === 1
  )

  // ==================== ESTADOS: Báscula ====================
  const [basculaConectada, setBasculaConectada] = useState(false)
  const [nombreBascula, setNombreBascula] = useState(null)
  // Input de medida actualmente seleccionado para recibir datos del socket (MEDIDA): 'form' + field name, o 'modal' + index
  const [activeMeasureTarget, setActiveMeasureTarget] = useState(null) // null | { type: 'form', field: 'largo'|'ancho'|'alto'|'diametro' } | { type: 'modal', index: number }
  // Si alguna medida (peso o largo/ancho/alto/diámetro/json_medidas) se llenó desde el socket Mitutoyo; al hacer PUT revisiones se envía es_mitutoyo: 1
  const [medidasRecibidasPorSocket, setMedidasRecibidasPorSocket] = useState(false)

  // ==================== ESTADOS: json_medidas (cuando viene de backend o plantilla) ====================
  const jsonMedidasFromMaterial = material?.revision_json_medidas ?? material?.json_medidas
  const [jsonMedidasEditable, setJsonMedidasEditable] = useState([]) // array de {valor, medida, nombre} o objeto plantilla { gs_url, especificaciones, imagen_referencia, subcategoria, categoria, definicion_cotas, valor }
  const [medidasBloqueadas, setMedidasBloqueadas] = useState(false)
  const [editandoMedidasGuardadas, setEditandoMedidasGuardadas] = useState(false)
  const [modalVerMedidasOpen, setModalVerMedidasOpen] = useState(false)
  const [jsonMedidasEnEdicion, setJsonMedidasEnEdicion] = useState([])
  const [imagenVerMedidasUrl, setImagenVerMedidasUrl] = useState(null) // una sola URL (legacy)
  const [imagenesVerMedidasUrls, setImagenesVerMedidasUrls] = useState([]) // varias imágenes para carrusel (signed_url de imagenes_procesadas)
  const [imagenesVerMedidasNombres, setImagenesVerMedidasNombres] = useState([]) // nombres de archivo (desde gs_url) para mostrar en el carrusel
  const [verMedidasCarouselIndex, setVerMedidasCarouselIndex] = useState(0)
  const carouselVerMedidasRef = useRef(null)
  const [opcionesMedidas, setOpcionesMedidas] = useState([])
  const [loadingPlantillaAlCambiarDescripcion, setLoadingPlantillaAlCambiarDescripcion] = useState(false)
  const tieneJsonMedidas =
    (Array.isArray(jsonMedidasEditable) && jsonMedidasEditable.length > 0) ||
    (!Array.isArray(jsonMedidasEditable) && jsonMedidasEditable && (jsonMedidasEditable.especificaciones?.length ?? 0) > 0) ||
    (Array.isArray(jsonMedidasFromMaterial) && jsonMedidasFromMaterial.length > 0) ||
    (jsonMedidasFromMaterial && !Array.isArray(jsonMedidasFromMaterial) && (jsonMedidasFromMaterial.especificaciones?.length ?? 0) > 0)
  // La imagen para cotización solo se puede pedir si el material ya tiene una
  // plantilla de medidas guardada en el backend y con al menos un valor.
  const plantillaMedidasGuardada =
    !!jsonMedidasFromMaterial &&
    !Array.isArray(jsonMedidasFromMaterial) &&
    Array.isArray(jsonMedidasFromMaterial.especificaciones) &&
    jsonMedidasFromMaterial.especificaciones.some(
      (e) => e.valor !== null && e.valor !== undefined && e.valor !== '' && e.valor !== 0 && e.valor !== '0',
    )
  // Editor manual de la imagen para cotización (cotas trazadas sobre las fotos)
  const [modalCotizacionOpen, setModalCotizacionOpen] = useState(false)
  const [imagenesPlantillaCotizacion, setImagenesPlantillaCotizacion] = useState([])
  const [cargandoPlantillaCotizacion, setCargandoPlantillaCotizacion] = useState(false)
  const prevCategoriaRef = useRef(null)
  const prevSubcategoriaRef = useRef(null)

  // ==================== ESTADOS: Canvas y Medidas ====================
  const [coordenadas, setCoordenadas] = useState([])
  const [medidasEditadas, setMedidasEditadas] = useState({})
  const [nombresEditados, setNombresEditados] = useState({})
  const [descripcionesEditadas, setDescripcionesEditadas] = useState({})
  const [unidadMedida, setUnidadMedida] = useState('mm')
  const [medidasManuales, setMedidasManuales] = useState([])
  const [contadorMedidasManuales, setContadorMedidasManuales] = useState(0)

  // ==================== ESTADOS: Modo Dibujo ====================
  const [modoDibujo, setModoDibujo] = useState(null) // null, 'puntoA', 'puntoB', 'circuloCentro', 'circuloRadio'
  const [puntoA, setPuntoA] = useState(null)
  const [puntoBTemporal, setPuntoBTemporal] = useState(null)
  const [puntoBConfirmado, setPuntoBConfirmado] = useState(false)
  const [centroCirculo, setCentroCirculo] = useState(null)
  const [puntoRadioTemporal, setPuntoRadioTemporal] = useState(null)
  const [radioConfirmado, setRadioConfirmado] = useState(false)

  // ==================== ESTADOS: Líneas de Referencia ====================
  const [medidaIndexParaLineaReferencia, setMedidaIndexParaLineaReferencia] = useState(null)
  const [puntoALineaReferencia, setPuntoALineaReferencia] = useState(null)
  const [puntoBLineaReferencia, setPuntoBLineaReferencia] = useState(null)
  const [puntoBLineaReferenciaConfirmado, setPuntoBLineaReferenciaConfirmado] = useState(false)
  const [lineaReferenciaSeleccionada, setLineaReferenciaSeleccionada] = useState(null) // { medidaIndex, lineaIndex }

  // ==================== ESTADOS: Selección y Arrastre ====================
  const [medidaSeleccionada, setMedidaSeleccionada] = useState(null) // medidaIndex de la medida seleccionada
  const [arrastrandoPunto, setArrastrandoPunto] = useState(null) // 'inicio' | 'fin' | null
  const [isDragging, setIsDragging] = useState(false)
  const [cursorHover, setCursorHover] = useState('default')

  // ==================== ESTADOS: Edición de Imagen ====================
  const [modalEdicionVisible, setModalEdicionVisible] = useState(false)
  const [isSelecting, setIsSelecting] = useState(false)
  const [selectionStart, setSelectionStart] = useState(null)
  const [selectionEnd, setSelectionEnd] = useState(null)
  const [rotacionAngulo, setRotacionAngulo] = useState(0)

  // ==================== ESTADOS: Cotas Guardadas ====================
  const [cotasGuardadas, setCotasGuardadas] = useState(null)
  const [todasLasCotasGuardadas, setTodasLasCotasGuardadas] = useState([])
  const [cotaSeleccionadaId, setCotaSeleccionadaId] = useState(null)
  const [modalCotasVisible, setModalCotasVisible] = useState(false)
  const [coordenadasCotas, setCoordenadasCotas] = useState([])
  const [medidasCotas, setMedidasCotas] = useState([])

  // ==================== ESTADOS: Recorte de Imagen ====================
  const [modalRecorteVisible, setModalRecorteVisible] = useState(false)
  const [recortando, setRecortando] = useState(false)
  const [gsurlRecortado, setGsurlRecortado] = useState(null)
  const [imagenOriginalParaRecorte, setImagenOriginalParaRecorte] = useState(null)
  // true cuando se abrió edición desde la foto (primer paso: editar/recortar → luego quitar fondo)
  const [flujoFotoPrimeroEdicion, setFlujoFotoPrimeroEdicion] = useState(false)

  const activeMeasureTargetRef = useRef(null)
  useEffect(() => {
    activeMeasureTargetRef.current = activeMeasureTarget
  }, [activeMeasureTarget])

  // ==================== REFS ====================
  const clickTimeoutRef = useRef(null)
  const canvasRef = useRef(null)
  const imageRef = useRef(null)
  const inputRefs = useRef({})
  const cropImageRef = useRef(null)
  const canvasCotasRef = useRef(null)
  const imageCotasRef = useRef(null)
  const coordenadasCotasRef = useRef([])
  const [clickCount, setClickCount] = useState(0)

  // Hook para dibujar en el canvas
  useCanvasDrawing({
    canvasRef,
    imageRef,
    imagenFirmadaUrl,
    modalImagenVisible,
    coordenadas,
    medidasManuales,
    medidaSeleccionada,
    lineaReferenciaSeleccionada,
    modoDibujo,
    puntoA,
    puntoBTemporal,
    puntoBConfirmado,
    centroCirculo,
    puntoRadioTemporal,
    radioConfirmado,
    medidaIndexParaLineaReferencia,
    puntoALineaReferencia,
    puntoBLineaReferencia,
    puntoBLineaReferenciaConfirmado,
  })

  useEffect(() => {
    if (imagenCotasUrl && canvasCotasRef.current && imageCotasRef.current && modalCotasVisible && coordenadasCotas && coordenadasCotas.length > 0) {
      const canvas = canvasCotasRef.current
      const img = imageCotasRef.current
      
      const drawCanvas = () => {
        const coordenadasActuales = coordenadasCotasRef.current
        
        if (!coordenadasActuales || coordenadasActuales.length === 0) {
          return
        }
        
        const rect = img.getBoundingClientRect()
        // Usar el tamaño real en píxeles de la imagen
        const displayWidth = rect.width
        const displayHeight = rect.height
        
        // Ajustar el tamaño interno del canvas (en píxeles)
        canvas.width = displayWidth
        canvas.height = displayHeight
        
        // Ajustar el tamaño visual del canvas para que coincida con la imagen
        canvas.style.width = `${displayWidth}px`
        canvas.style.height = `${displayHeight}px`
        
        const ctx = canvas.getContext('2d')
        ctx.clearRect(0, 0, canvas.width, canvas.height)
        
        ctx.strokeStyle = '#800080'
        ctx.lineWidth = 2
        ctx.setLineDash([5, 5])
        ctx.strokeRect(1, 1, canvas.width - 2, canvas.height - 2)
        
        const escalaX = canvas.width / 1000
        const escalaY = canvas.height / 1000

        if (coordenadasActuales && coordenadasActuales.length > 0) {
          // PRIMERO dibujar las medidas principales (geometria_cota) en púrpura SIN flechas
          ctx.setLineDash([]) // Asegurar línea sólida
          ctx.strokeStyle = '#800080' // Púrpura
          ctx.lineWidth = 1
          
          coordenadasActuales.forEach((cota) => {
            // Si tiene geometria_cota como objeto (caso actual), dibujar la línea
            if (cota.geometria_cota && !Array.isArray(cota.geometria_cota)) {
              const geometria = cota.geometria_cota
              if (geometria.x_ini !== undefined && geometria.y_ini !== undefined && 
                  geometria.x_fin !== undefined && geometria.y_fin !== undefined) {
                ctx.beginPath()
                ctx.moveTo(geometria.x_ini * escalaX, geometria.y_ini * escalaY)
                ctx.lineTo(geometria.x_fin * escalaX, geometria.y_fin * escalaY)
                ctx.stroke()
              }
            }
            // Si tiene geometria_cota como array, dibujar cada línea
            else if (cota.geometria_cota && Array.isArray(cota.geometria_cota)) {
              cota.geometria_cota.forEach((geometria) => {
                if (geometria && geometria.x_ini !== undefined && geometria.y_ini !== undefined && 
                    geometria.x_fin !== undefined && geometria.y_fin !== undefined) {
                  ctx.beginPath()
                  ctx.moveTo(geometria.x_ini * escalaX, geometria.y_ini * escalaY)
                  ctx.lineTo(geometria.x_fin * escalaX, geometria.y_fin * escalaY)
                  ctx.stroke()
                }
              })
            }
            // Mantener compatibilidad con el formato anterior (coordenada)
            else if (cota.coordenada) {
              const coords = cota.coordenada
              
              if (coords.x_ini !== undefined && coords.y_ini !== undefined && 
                  coords.x_fin !== undefined && coords.y_fin !== undefined) {
                ctx.beginPath()
                ctx.moveTo(coords.x_ini * escalaX, coords.y_ini * escalaY)
                ctx.lineTo(coords.x_fin * escalaX, coords.y_fin * escalaY)
                ctx.stroke()
              }
              else if (coords.x !== undefined && coords.y !== undefined && coords.radio !== undefined) {
                const escalaRadio = (escalaX + escalaY) / 2
                ctx.beginPath()
                ctx.arc(
                  coords.x * escalaX,
                  coords.y * escalaY,
                  coords.radio * escalaRadio,
                  0,
                  2 * Math.PI
                )
                ctx.stroke()
              }
              else if (coords.x !== undefined && coords.y !== undefined) {
                ctx.fillStyle = '#800080'
                ctx.beginPath()
                ctx.arc(coords.x * escalaX, coords.y * escalaY, 5, 0, 2 * Math.PI)
                ctx.fill()
              }
            }
          })
          
          // LUEGO dibujar las líneas de referencia (lineas_a_repuesto) en rojo CON flechas
          ctx.setLineDash([])
          ctx.strokeStyle = '#8B0000' // Rojo sangre
          ctx.lineWidth = 1
          
          coordenadasActuales.forEach((cota, cotaIndex) => {
            // Dibujar lineas_a_repuesto si existen
            if (cota.lineas_a_repuesto && Array.isArray(cota.lineas_a_repuesto)) {
              cota.lineas_a_repuesto.forEach((linea) => {
                if (linea && linea.x_ini !== undefined && linea.y_ini !== undefined && 
                    linea.x_fin !== undefined && linea.y_fin !== undefined) {
                  const x1 = linea.x_ini * escalaX
                  const y1 = linea.y_ini * escalaY
                  const x2 = linea.x_fin * escalaX
                  const y2 = linea.y_fin * escalaY
                  
                  dibujarFlecha(ctx, x1, y1, x2, y2)
                }
              })
            }
          })
          
          // Dibujar las etiquetas (labels) en las coordenadas_etiqueta
          ctx.fillStyle = '#800080' // Púrpura para el texto
          ctx.font = 'bold 16px Arial'
          ctx.textAlign = 'center'
          ctx.textBaseline = 'middle'
          
          coordenadasActuales.forEach((cota) => {
            // Usar la función extraerLetraLabel para obtener la letra
            const letraLabel = extraerLetraLabel(cota)
            
            // Dibujar el label en coordenadas_etiqueta si existe
            if (letraLabel && cota.coordenadas_etiqueta) {
              const etiqueta = cota.coordenadas_etiqueta
              if (etiqueta.x !== undefined && etiqueta.y !== undefined) {
                const xEtiqueta = etiqueta.x * escalaX
                const yEtiqueta = etiqueta.y * escalaY
                
                // Dibujar el texto
                ctx.fillStyle = '#800080'
                ctx.fillText(letraLabel, xEtiqueta, yEtiqueta)
              }
            }
          })
        }
      }
      
      // Si la imagen ya está cargada, dibujar inmediatamente
      if (img.complete && img.naturalWidth > 0) {
        // Usar setTimeout para asegurar que el DOM esté actualizado
        setTimeout(drawCanvas, 0)
      } else {
        // Esperar a que la imagen se cargue
        img.onload = drawCanvas
      }
      
      const resizeObserver = new ResizeObserver(() => {
        const coordenadasActuales = coordenadasCotasRef.current
        if (img.complete && coordenadasActuales && coordenadasActuales.length > 0) {
          // Usar requestAnimationFrame para asegurar que el DOM esté actualizado
          requestAnimationFrame(() => {
            drawCanvas()
          })
        }
      })
      
      // Observar tanto la imagen como su contenedor
      if (img.parentElement) {
        resizeObserver.observe(img.parentElement)
      }
      resizeObserver.observe(img)
      
      return () => {
        resizeObserver.disconnect()
      }
    }
  }, [imagenCotasUrl, modalCotasVisible, coordenadasCotas])

  // Conectar socket automáticamente cuando se abre el modal
  useEffect(() => {
    const conectarBascula = async () => {
      if (idUser) {
        try {
          // Obtener la báscula del usuario
          const resultado = await getBasculaPorUsuario(idUser)
          console.log('Resultado de báscula:', resultado)

          if (resultado && resultado.bascula && resultado.bascula.nombre) {
            const basculaNombre = resultado.bascula.nombre
            setNombreBascula(basculaNombre)

            // Conectar al WebSocket
            basculaWebSocketService.connect(
              basculaNombre,
              (message) => {
                // Callback para mensajes recibidos
                console.log('📥 Mensaje de báscula recibido:', message)
                
                // Si el mensaje es de tipo PESO, actualizar el campo del formulario
                if (message.type === 'PESO' && message.data !== undefined) {
                  form.setFieldsValue({
                    peso: message.data
                  })
                  setMedidasRecibidasPorSocket(true)
                  console.log('✅ Peso actualizado en el formulario:', message.data)
                  
                  // Si el campo estaba en camposFaltantes, removerlo usando función de actualización
                  setCamposFaltantes(prev => {
                    if (prev.includes('peso')) {
                      return prev.filter(c => c !== 'peso')
                    }
                    return prev
                  })
                }

                // Si el mensaje es de tipo MEDIDA, llenar el input de medida que esté seleccionado (clickeado)
                const target = activeMeasureTargetRef.current
                if (message.type === 'MEDIDA' && message.data != null && target) {
                  setMedidasRecibidasPorSocket(true)
                  const valor = typeof message.data === 'number' ? message.data : parseFloat(message.data)
                  if (target.type === 'form' && target.field) {
                    form.setFieldsValue({ [target.field]: Number.isNaN(valor) ? message.data : valor })
                    setCamposFaltantes(prev => prev.includes(target.field) ? prev.filter(c => c !== target.field) : prev)
                  }
                  if (target.type === 'modal' && typeof target.index === 'number') {
                    setJsonMedidasEnEdicion(prev => prev.map((m, i) => i === target.index ? { ...m, valor: Number.isNaN(valor) ? message.data : valor } : m))
                  }
                }
                
                // Si el mensaje es de tipo CAMARA, mostrar la respuesta en consola
                if (message.type === 'CAMARA') {
                  console.log('📸 Respuesta de cámara recibida:', message)
                  // Si la foto fue subida exitosamente, mostrar modal para decidir si mejorar
                  if (message.data && message.data.status === 'subida' && message.data.gs_uri) {
                    console.log('✅ Foto subida exitosamente:', message.data.gs_uri)
                    
                    const gsUri = message.data.gs_uri
                    
                    // Firmar la revisión y abrir modal de medidas
                    postFirmarRevision(gsUri).then((response) => {
                      console.log('📝 Resultado de firmar revisión:', response)
                      if (response && response.url) {
                        console.log('✅ Revisión firmada exitosamente')
                        abrirModalMedidas(gsUri, response.url)
                      } else {
                        console.error('❌ Error al firmar revisión:', response)
                        toast.error('Error al procesar la imagen')
                      }
                    }).catch((error) => {
                      console.error('❌ Error al firmar revisión:', error)
                      toast.error('Error al procesar la imagen')
                    })
                    
                    handleGetImagesByMaterial()
                  }
                }
              },
              (isConnected) => {
                // Callback para cambios de estado
                setBasculaConectada(isConnected)
                if (!isConnected) {
                  setNombreBascula(null)
                }
              }
            )
          } else {
            console.log('No se encontró báscula asignada al usuario')
          }
        } catch (error) {
          console.error('Error al conectar báscula:', error)
        }
      }
    }

    conectarBascula()

    // Limpiar conexión al desmontar el componente o cuando se cierra el modal
    return () => {
      basculaWebSocketService.disconnect()
      setBasculaConectada(false)
      setNombreBascula(null)
    }
  }, [idUser]) // Solo ejecutar cuando cambie idUser o al montar

  // Espejo consolidado: auditor y axiomer solo pueden ver el material, sin editar
  const isConsolidadoEspejo =
    material?.repuesto_consolidado_espejo === true ||
    material?.repuesto_consolidado_espejo === 1 ||
    material?.respuesta_consolidado_espejo === true ||
    material?.respuesta_consolidado_espejo === 1

  // Determinar si el formulario debe estar en modo solo lectura
  const isReadOnly =
    isConsolidadoEspejo ||
    (!isAuditor &&
      (material.estado === 'REVISADO' ||
        material.estado === 'FINALIZADO' ||
        material.estado === 'AUDITADO' ||
        (material.estado === 'ASIGNADO' &&
          idUser !== material.revision_usuario_revisor_id)))

  // ==================== FUNCIONES: API y Datos ====================
  const handleGetReferenciasRepuesto = async (repuestoId) => {
    let homologosList = []
    let reemplazantesList = []
    let modelosAplicacionPrincipalList = []
    let modelosAplicacionList = []

    const response = await getReferenciasRepuesto(repuestoId)

    if (response?.relaciones?.homologos_sugeridos?.length > 0) {
      homologosList = [
        ...homologosList,
        ...response.relaciones.homologos_sugeridos,
      ]
    }

    if (response?.relaciones?.reemplazantes_sugeridos?.length > 0) {
      reemplazantesList = [
        ...reemplazantesList,
        ...response.relaciones.reemplazantes_sugeridos,
      ]
    }

    if (response?.relaciones?.modelos_aplicacion_ppal_sugeridos?.length > 0) {
      modelosAplicacionPrincipalList = [
        ...modelosAplicacionPrincipalList,
        ...response.relaciones.modelos_aplicacion_ppal_sugeridos,
      ]
    }

    if (response?.relaciones?.modelos_aplicacion_sugeridos?.length > 0) {
      modelosAplicacionList = [
        ...modelosAplicacionList,
        ...response.relaciones.modelos_aplicacion_sugeridos,
      ]
    }

    setHomologos(homologosList)
    setReemplazantes(reemplazantesList)
    setModelosAplicacionPrincipal(modelosAplicacionPrincipalList)
    setModelosAplicacion(modelosAplicacionList)
  }

  useEffect(() => {
    setTiposEmbalajeLocal(tiposEmbalaje || [])
  }, [tiposEmbalaje])

  useEffect(() => {
    setTiposEmpaqueLocal(tiposEmpaque || [])
  }, [tiposEmpaque])

  useEffect(() => {
    setTiposProteccionLocal(tiposProteccion || [])
  }, [tiposProteccion])

  useEffect(() => {
    setMarcasFabricaLocal(marcasFabrica || [])
  }, [marcasFabrica])

  const marcasFabricaCatalogRef = useRef(marcasFabricaLocal)
  marcasFabricaCatalogRef.current = marcasFabricaLocal

  // Sincronizar marca fábrica con el material (sin depender del catálogo en el array de deps para no pisar al agregar una marca nueva).
  useEffect(() => {
    if (!material) return
    const id = getMarcaFabricaIdFromMaterial(material, marcasFabricaCatalogRef.current)
    form.setFieldsValue({ marcaFabrica: id })
  }, [
    material?.revision_id,
    material?.repuesto_id,
    material?.revision_marca_fabrica_nombre,
    material?.revision_marca_fabrica_id,
    material?.revision_marca_fabrica_ids,
    material?.revision_marcas_fabrica_ids,
    material?.revision_marcas_fabrica,
    form,
  ])

  // Si el backend solo envía nombre(s) y el catálogo llega después, hidratar cuando aún no hay selección en el formulario.
  useEffect(() => {
    if (!material) return
    const tieneIdsExplicitos =
      (Array.isArray(material.revision_marca_fabrica_ids) && material.revision_marca_fabrica_ids.length > 0) ||
      (Array.isArray(material.revision_marcas_fabrica_ids) && material.revision_marcas_fabrica_ids.length > 0) ||
      (Array.isArray(material.revision_marcas_fabrica) && material.revision_marcas_fabrica.length > 0) ||
      (material.revision_marca_fabrica_id != null && material.revision_marca_fabrica_id !== '')
    if (tieneIdsExplicitos) return
    if (!material.revision_marca_fabrica_nombre) return
    const id = getMarcaFabricaIdFromMaterial(material, marcasFabricaLocal)
    const current = form.getFieldValue('marcaFabrica')
    if (current != null && current !== '') return
    if (id == null) return
    form.setFieldsValue({ marcaFabrica: id })
  }, [
    marcasFabricaLocal,
    material?.revision_id,
    material?.repuesto_id,
    material?.revision_marca_fabrica_nombre,
    material?.revision_marca_fabrica_id,
    material?.revision_marca_fabrica_ids,
    material?.revision_marcas_fabrica_ids,
    material?.revision_marcas_fabrica,
    form,
  ])

  useEffect(() => {
    // Reinicializar los campos de IA desde el material cada vez que cambia
    setDescripcionBreveIA(material?.revision_descripcion_breve_ia || '')
    setDescripcionBreveIAModificada(
      material?.revision_descripcion_breve_ia_modificada !== undefined 
        ? material.revision_descripcion_breve_ia_modificada 
        : 0
    )
    setDescripcionUrgente(material?.revision_descripcion_correcta_urgente === 1)
    setImagenFavorita(material?.revision_imagen_favorita || null)
    setRequiereActualizacionCatalogo(material?.revision_requiere_actualizacion_catalogo ?? 0)
  }, [material?.repuesto_id, material?.revision_id, material?.revision_descripcion_breve_ia, material?.revision_descripcion_breve_ia_modificada, material?.revision_descripcion_correcta_urgente, material?.revision_imagen_favorita, material?.revision_requiere_actualizacion_catalogo])

  useEffect(() => {
    const fromMaterial = material?.revision_json_medidas ?? material?.json_medidas
    if (Array.isArray(fromMaterial) && fromMaterial.length > 0) {
      setJsonMedidasEditable(fromMaterial.map(m => ({ valor: m.valor, medida: m.medida ?? '', nombre: m.nombre ?? '' })))
      const tieneValores = fromMaterial.some(m => m.valor !== null && m.valor !== undefined && m.valor !== '' && m.valor !== 0 && m.valor !== '0')
      setMedidasBloqueadas(tieneValores)
    } else if (fromMaterial && !Array.isArray(fromMaterial) && fromMaterial.especificaciones) {
      setJsonMedidasEditable(fromMaterial)
      const tieneValores = Array.isArray(fromMaterial.especificaciones) && fromMaterial.especificaciones.some(e => e.valor !== null && e.valor !== undefined && e.valor !== '' && e.valor !== 0 && e.valor !== '0')
      setMedidasBloqueadas(tieneValores)
    } else {
      setJsonMedidasEditable([])
      setMedidasBloqueadas(false)
    }
  }, [material?.revision_json_medidas, material?.json_medidas])

  useEffect(() => {
    prevCategoriaRef.current = material?.revision_categoria_sugerida_id
    prevSubcategoriaRef.current = material?.revision_subcategoria_sugerida_id
  }, [material?.revision_categoria_sugerida_id, material?.revision_subcategoria_sugerida_id])

  // Cascada por sector: solo corre si HABILITAR_JERARQUIA_CASCADA es true.
  // Con el flag en false las listas quedan libres (props completas del contenedor).
  useEffect(() => {
    const iniciarCascada = async () => {
      const resuelto = await cascada.iniciar(material)
      if (resuelto) {
        form.setFieldsValue({
          categoria: resuelto.categoriaId,
          subcategoria: resuelto.subcategoriaId,
          segmento: resuelto.segmentoId,
          subsegmento: resuelto.subsegmentoId,
        })
      }
    }
    iniciarCascada()
  }, [material?.repuesto_id, material?.sector])

  // Al cambiar de revisión, resetear flag de medidas por socket (cada revisión lleva su propio estado)
  useEffect(() => {
    setMedidasRecibidasPorSocket(false)
  }, [material?.revision_id])

  useEffect(() => {
    if (material.estado === 'PENDIENTE POR ASIGNAR') {
      confirm({
        title:
          'Este material no está asignado a una revisión, ¿deseas asignartelo?',
        onOk: async () => {
          if (!material.revision_id) {
            const response = await putAsignarMaterialRevisionSTG({
              repuestoId: material.repuesto_id,
              usuarioId: idUser,
            })
            if (response && response.success) {
              onResetData?.()
            }
          } else {
            const data = {
              revision_id: material.revision_id,
              usuario_id: idUser,
              estado_revision: 'REASIGNADO',
              ...material,
              ...(medidasRecibidasPorSocket ? { es_mitutoyo: 1 } : {}),
            }
            const responseUpdate = await putUpdateRevision(data)
            if (responseUpdate && responseUpdate.success) {
              onResetData?.()
            } else {
              toast.error('Error al actualizar la revisión')
            }
          }
        },
        onCancel: () => {
          onClose?.()
        },
      })
    } else {
      handleGetReferenciasRepuesto(material.revision_id)
    }
  }, [material])

  const handleGetImagesByMaterial = async () => {
    const response = await getImagesByMaterial(
      material.repuesto_codigo_material_sap,
    )
    if (response && response.success) {
      setImages(response.success)
      const conUrl = response.success.filter(img => img?.signed_url)
      // Sin favorita no se inventa una. Solo si había favorita y esa foto ya no
      // existe se pasa a otra, para no dejar el material sin favorita.
      setImagenFavorita(prev =>
        prev && !conUrl.some(img => img.nombre_objeto === prev)
          ? conUrl[0]?.nombre_objeto ?? null
          : prev,
      )
      console.log(response.success)
    }
    setFotosMaterialListas(true)
  }

  const photosCount = images.filter((img) => img?.signed_url).length
  // La imagen para cotización puede armarse con fotos del material o del empaque.
  const hayFotosCotizacion = [...images, ...imagenesEmpaque].some((img) => img?.signed_url)
  const tieneFotosMaterial = photosCount > 0

  useEffect(() => {
    prevPhotosCountRef.current = null
    setFotosMaterialListas(false)
    setBuscarReemplazantesCooldown(0)
    if (buscarReemplazantesTimerRef.current) {
      clearInterval(buscarReemplazantesTimerRef.current)
      buscarReemplazantesTimerRef.current = null
    }
  }, [material?.repuesto_codigo_material_sap])

  useEffect(() => {
    if (!fotosMaterialListas) return undefined
    if (prevPhotosCountRef.current === null) {
      prevPhotosCountRef.current = photosCount
      return undefined
    }
    if (prevPhotosCountRef.current === 0 && photosCount > 0) {
      setBuscarReemplazantesCooldown(10)
      if (buscarReemplazantesTimerRef.current) {
        clearInterval(buscarReemplazantesTimerRef.current)
      }
      buscarReemplazantesTimerRef.current = setInterval(() => {
        setBuscarReemplazantesCooldown((prev) => {
          if (prev <= 1) {
            if (buscarReemplazantesTimerRef.current) {
              clearInterval(buscarReemplazantesTimerRef.current)
              buscarReemplazantesTimerRef.current = null
            }
            return 0
          }
          return prev - 1
        })
      }, 1000)
    }
    prevPhotosCountRef.current = photosCount
    return undefined
  }, [photosCount, fotosMaterialListas])

  useEffect(
    () => () => {
      if (buscarReemplazantesTimerRef.current) {
        clearInterval(buscarReemplazantesTimerRef.current)
      }
    },
    [],
  )

  const normalizarCatalogoImagen = (response) => {
    if (!response) return null
    if (response.success && !Array.isArray(response.success)) return response.success
    if (Array.isArray(response.success)) return response.success[0] || null
    if (Array.isArray(response.data)) return response.data[0] || null
    if (response.data && !Array.isArray(response.data)) return response.data
    if (Array.isArray(response)) return response[0] || null
    return response.signed_url ? response : null
  }

  const handleGetCatalogUpdateImage = async () => {
    if (!material?.revision_id) {
      setCatalogUpdateImage(null)
      return
    }
    const response = await getImagenArgumentacionCatalogo(material.revision_id)
    setCatalogUpdateImage(normalizarCatalogoImagen(response))
  }

  const handleUploadCatalogUpdateImage = async (imageFile) => {
    if (!material?.revision_id) {
      toast.error('No se encontró la revisión para subir la imagen')
      return false
    }
    const response = await postImagenArgumentacionCatalogo(material.revision_id, imageFile)
    if (response) {
      handleGetCatalogUpdateImage()
    }
    return false
  }

  const handleDeleteCatalogUpdateImage = async () => {
    if (!material?.revision_id) return
    const response = await deleteImagenArgumentacionCatalogo(material.revision_id)
    if (response) {
      setCatalogUpdateImage(null)
      handleGetCatalogUpdateImage()
    }
  }

  useEffect(() => {
    handleGetImagesByMaterial()
    handleGetImagenesEmpaque()
    handleGetImagenCotizacion()
  }, [])

  useEffect(() => {
    handleGetCatalogUpdateImage()
  }, [material?.revision_id])

  // ==================== FUNCIONES: Relaciones y Tags ====================
  useEffect(() => {
    const cargarCotasGuardadas = async () => {
      if (material?.revision_id) {
        const response = await getImagenCotas(material.revision_id)
        if (response && response.success && response.success.length > 0) {
          // Guardar todas las cotas ordenadas por id descendente
          const cotasOrdenadas = response.success.sort((a, b) => b.id - a.id)
          setTodasLasCotasGuardadas(cotasOrdenadas)
          // Obtener el último item (mayor id) para mantener compatibilidad
          const ultimaCota = cotasOrdenadas[0]
          setCotasGuardadas(ultimaCota)
          setCotaSeleccionadaId(ultimaCota.id) // Establecer la última como seleccionada por defecto
        } else {
          setCotasGuardadas(null)
          setTodasLasCotasGuardadas([])
          setCotaSeleccionadaId(null)
        }
      } else {
        setCotasGuardadas(null)
        setTodasLasCotasGuardadas([])
        setCotaSeleccionadaId(null)
      }
    }
    cargarCotasGuardadas()
  }, [material?.revision_id])

  // ==================== CONSTANTES ====================
  const estadosDefinidos = ['ACTIVO', 'INACTIVO', 'OBSOLETO']
  const cambioOpciones = ['No', 'Sí']

  // ==================== FUNCIONES: Utilidades ====================
  const convertirBooleanATexto = (valor) => {
    if (valor === true) return 'Sí'
    if (valor === false) return 'No'
    return valor // Si no es boolean, devolver el valor original
  }

  const handleRemoveTag = async (repuestoId, category) => {
    if (category === 'Homólogo') {
      const response = await putDesvincularHomologo({
        revisionId: material.revision_id,
        homologoId: repuestoId,
      })
      if (response && response.success) {
        // Eliminar del estado local
        setHomologos((prev) =>
          prev.filter((item) => item.repuesto_id !== repuestoId),
        )
        toast.success('Homólogo desvinculado exitosamente')
      } else {
        toast.error(
          response?.message || 'Error al desvincular el homólogo',
        )
      }
    }
    if (category === 'Reemplazante') {
      const response = await putDesvincularReemplazante({
        revisionId: material.revision_id,
        reemplazanteId: repuestoId,
      })
      if (response && response.success) {
        // Eliminar del estado local
        setReemplazantes((prev) =>
          prev.filter((item) => item.repuesto_id !== repuestoId),
        )
        toast.success('Reemplazante desvinculado exitosamente')
      } else {
        toast.error(
          response?.message || 'Error al desvincular el reemplazante',
        )
      }
    }
    if (category === 'Modelo Aplicación') {
      const response = await putDesvincularModelo({
        revisionId: material.revision_id,
        modeloId: repuestoId,
        esPrincipal: 0,
      })
      if (response && response.success) {
        // Eliminar del estado local
        setModelosAplicacion((prev) =>
          prev.filter((item) => item.id !== repuestoId),
        )
        toast.success('Modelo de aplicación desvinculado exitosamente')
      } else {
        toast.error(
          response?.message || 'Error al desvincular el modelo de aplicación',
        )
      }
    }
    if (category === 'Modelo Aplicación Principal') {
      console.log('desvincular modelo principal', repuestoId)
      const response = await putDesvincularModelo({
        revisionId: material.revision_id,
        modeloId: repuestoId,
        esPrincipal: 1,
      })
      if (response && response.success) {
        // Eliminar del estado local
        setModelosAplicacionPrincipal((prev) =>
          prev.filter((item) => item.id !== repuestoId),
        )
        toast.success('Modelo de aplicación principal desvinculado exitosamente')
      } else {
        toast.error(
          response?.message ||
            'Error al desvincular el modelo de aplicación principal',
        )
      }
    }
  }

  const handleMatchReemplazanteFromBusqueda = async (repuesto) => {
    if (isReadOnly) {
      toast.warning('No puede agregar reemplazantes en modo solo lectura')
      return false
    }
    if (!repuesto?.repuesto_id) {
      toast.error('El candidato no tiene un repuesto válido')
      return false
    }
    const yaExiste = reemplazantes.some(
      (r) => r.repuesto_id === repuesto.repuesto_id,
    )
    if (yaExiste) {
      return false
    }

    const response = await putRelacionReemplazante({
      revisionId: material.revision_id,
      reemplazanteId: repuesto.repuesto_id,
      origen: 'MOMENTOS_HU',
    })

    if (response && response.success) {
      const nuevoReemplazante = {
        repuesto_id: repuesto.repuesto_id,
        codigo_material_sap: repuesto.repuesto_codigo_material_sap,
        npf: repuesto.repuesto_npf,
        descripcion: repuesto.repuesto_descripcion,
        fabricante: repuesto.repuesto_fabricante,
        origen: 'MOMENTOS_HU',
      }
      setReemplazantes((prev) => [...prev, nuevoReemplazante])
      toast.success('Reemplazante agregado exitosamente')
      return true
    }

    toast.error(
      response?.message || 'Error al agregar el reemplazante',
    )
    return false
  }

  const handleUnmatchReemplazanteFromBusqueda = async (repuesto) => {
    if (isReadOnly) {
      toast.warning('No puede modificar reemplazantes en modo solo lectura')
      return false
    }
    if (!repuesto?.repuesto_id) {
      toast.error('El candidato no tiene un repuesto válido')
      return false
    }

    const response = await putDesvincularReemplazante({
      revisionId: material.revision_id,
      reemplazanteId: repuesto.repuesto_id,
    })

    if (response && response.success) {
      setReemplazantes((prev) =>
        prev.filter((r) => r.repuesto_id !== repuesto.repuesto_id),
      )
      toast.success('Reemplazante desvinculado exitosamente')
      return true
    }

    toast.error(
      response?.message || 'Error al desvincular el reemplazante',
    )
    return false
  }

  const handleMatchSustitutoFromBusqueda = async (repuesto) => {
    if (isReadOnly) {
      toast.warning('No puede agregar sustitutos en modo solo lectura')
      return false
    }
    if (!repuesto?.repuesto_id) {
      toast.error('El candidato no tiene un repuesto válido')
      return false
    }
    const yaExiste = homologos.some(
      (h) => h.repuesto_id === repuesto.repuesto_id,
    )
    if (yaExiste) {
      return false
    }

    const response = await putRelacionHomologo({
      revisionId: material.revision_id,
      homologoId: repuesto.repuesto_id,
      origen: 'MOMENTOS_HU',
    })

    if (response && response.success) {
      const nuevoSustituto = {
        repuesto_id: repuesto.repuesto_id,
        codigo_material_sap: repuesto.repuesto_codigo_material_sap,
        npf: repuesto.repuesto_npf,
        descripcion: repuesto.repuesto_descripcion,
        fabricante: repuesto.repuesto_fabricante,
        origen: 'MOMENTOS_HU',
      }
      setHomologos((prev) => [...prev, nuevoSustituto])
      toast.success('Sustituto agregado exitosamente')
      return true
    }

    toast.error(response?.message || 'Error al agregar el sustituto')
    return false
  }

  const handleUnmatchSustitutoFromBusqueda = async (repuesto) => {
    if (isReadOnly) {
      toast.warning('No puede modificar sustitutos en modo solo lectura')
      return false
    }
    if (!repuesto?.repuesto_id) {
      toast.error('El candidato no tiene un repuesto válido')
      return false
    }

    const response = await putDesvincularHomologo({
      revisionId: material.revision_id,
      homologoId: repuesto.repuesto_id,
    })

    if (response && response.success) {
      setHomologos((prev) =>
        prev.filter((h) => h.repuesto_id !== repuesto.repuesto_id),
      )
      toast.success('Sustituto desvinculado exitosamente')
      return true
    }

    toast.error(response?.message || 'Error al desvincular el sustituto')
    return false
  }

  const handleAddTag = (category) => {
    setCategorytoCompare(category)
  }

  const handleAddTagModelMoto = (category) => {
    setCategorytoCompareModelMoto(category)
  }

  // Función helper para validar si un campo está faltante
  const getFieldValidation = (fieldName) => {
    if (camposFaltantes.includes(fieldName)) {
      return {
        validateStatus: 'error',
        help: ''
      }
    }
    return {}
  }

  // Función para generar texto breve usando IA
  // ==================== FUNCIONES: Formulario y Validación ====================
  const handleGenerarTextoBreve = async () => {
    const descripcionCorrecta = form.getFieldValue('descripcionCorrecta')
    
    if (!descripcionCorrecta || descripcionCorrecta.length < 5) {
      toast.warning('La descripción correcta debe tener al menos 5 caracteres')
      return
    }

    setLoadingTextoBreve(true)
    try {
      const response = await postGenerarTextoBreve(descripcionCorrecta)

      if (response && response.success && response.texto_breve) {
        const textoBreveGenerado = response.texto_breve
          .toUpperCase()
        form.setFieldsValue({
          descripcionBreve: textoBreveGenerado,
        })
        // Guardar la respuesta de la IA en los estados
        setDescripcionBreveIA(textoBreveGenerado)
        setDescripcionBreveIAModificada(0) // 0 = no modificado
        
        // Limpiar el error si existe
        if (camposFaltantes.includes('descripcionBreve')) {
          setCamposFaltantes(camposFaltantes.filter(c => c !== 'descripcionBreve'))
        }
        toast.success('Texto breve generado exitosamente')
      } else {
        toast.error('No se pudo generar el texto breve')
      }
    } catch (error) {
      console.error('Error al generar texto breve:', error)
      toast.error('Error al generar el texto breve')
    } finally {
      setLoadingTextoBreve(false)
    }
  }

  const handlePlantillaMedidas = async (categoriaId, subcategoriaId, definicionCotas) => {
    const raw = await getPlantillaMedidas(categoriaId, subcategoriaId, definicionCotas)
    const response = raw?.data ?? raw
    if (!response) {
      setJsonMedidasEditable([])
      return []
    }
    const { especificaciones, imagenes_procesadas: imagenesProcesadas } = response
    if (!Array.isArray(especificaciones) || especificaciones.length === 0) {
      setJsonMedidasEditable([])
      return []
    }
    const categoriaNombre = categorias?.find(c => c.id === categoriaId)?.nombre ?? ''
    const subcategoriaNombre = subCategorias?.find(s => s.id === subcategoriaId)?.nombre ?? ''
    const gsUrl = response.gs_url ?? ''
    const imagenReferencia = response.imagen_referencia ?? (gsUrl ? gsUrl.replace(/^.*\//, '').replace(/\.[^.]+$/, '') : '')
    const jsonMedidas = {
      gs_url: gsUrl,
      imagenes_procesadas: Array.isArray(imagenesProcesadas) ? imagenesProcesadas : [],
      especificaciones: especificaciones.map(e => ({
        medida: e.medida ?? '',
        descripcion: e.definicion_cotas ?? e.descripcion ?? '',
        definicion_cotas: e.definicion_cotas ?? e.descripcion ?? '',
        tipo_campo: e.tipo_campo ?? 'Num',
        opciones: e.opciones ?? '', // pipe-separated para ARR, ej: "3X2|4X3|5X4"
        valor: e.tipo_campo === 'ARR' ? '' : 0,
      })),
      imagen_referencia: imagenReferencia,
      subcategoria: subcategoriaNombre,
      categoria: categoriaNombre,
      definicion_cotas: definicionCotas ?? '',
      valor: 0,
    }
    setJsonMedidasEditable(jsonMedidas)
    return jsonMedidas
  }


  const handleSubmitSave = async (values, finalizar, retornar, overrideJsonMedidas = null) => {
 console.log(material)
    // todos los campos son requeridos (cuando "Ver medidas" está activo, 4 campos dejan de ser requeridos)
    if(finalizar){
      const camposRequeridosBase = [
        'descripcionBreve',
        'descripcionCorrecta',
        'observacion',
        'cambioEtiqueta',
        'cambioEmpaque',
        'categoria',
        'subcategoria',
        'segmento',
        'subsegmento',
        'marca',
        'estadoDefinido',
        'usoAplicacion',
        'largo',
        'ancho',
        'alto',
        'peso',
        'especificacionesElectricas',
        'especificacionesProducto',
        'tipoEmpaque',
        'proteccion',
        'embalaje'
      ]
      // Con "Ver medidas" activo: Largo, Ancho, Alto no son requeridos (se ocultan en la UI)
      const camposOpcionalesConVerMedidas = ['largo', 'ancho', 'alto']
      const camposRequeridos = tieneJsonMedidas
        ? camposRequeridosBase.filter(c => !camposOpcionalesConVerMedidas.includes(c))
        : camposRequeridosBase

      const camposFaltantesList = camposRequeridos.filter(campo => {
        const value = values[campo];
        return esCampoRequeridoFaltante(campo, value);
      })

      console.log(modelosAplicacion)
      if (
        values.descripcionCorrecta &&
        values.descripcionCorrecta.length > DESCRIPCION_CORRECTA_MAX_LENGTH
      ) {
        toast.error(
          `La descripción correcta no puede superar ${DESCRIPCION_CORRECTA_MAX_LENGTH} caracteres`,
        )
        setCamposFaltantes((prev) =>
          prev.includes('descripcionCorrecta')
            ? prev
            : [...prev, 'descripcionCorrecta'],
        )
        return
      }

      if(modelosAplicacion.length === 0){
        toast.error(`Faltan modelos de aplicación`)
        return
      }
      if(modelosAplicacionPrincipal.length === 0){
        toast.error(`Faltan modelos de aplicación principal`)
        return
      }
      
      // Validar que haya al menos una imagen
      if(images.length === 0 || !images.some(img => img?.signed_url)){
        toast.error(`Debe subir al menos una imagen del material`)
        return
      }
      
      if(camposFaltantesList.length > 0 ){
        setCamposFaltantes(camposFaltantesList)
        console.log('camposFaltantesList', camposFaltantesList)
        toast.error(`Faltan ${camposFaltantesList.length} campos requeridos`)
        return
      }
      
      // Si todos los campos están completos, limpiar el estado
      setCamposFaltantes([])
    }
    
    const data = {
      revision_id: material.revision_id || null,
      repuesto_id: material.repuesto_id || null,
      usuario_id: (finalizar && isAuditor) ? material.revision_usuario_revisor_id  : finalizar ? idUser : 
       retornar
        ? material.revision_usuario_revisor_id || null
        : material.revision_usuario_revisor_id  || null,
      estado_revision: finalizar
        ? isAuditor
          ? 'AUDITADO'
          : 'REVISADO'
        : retornar
        ? 'REASIGNADO'
        : material.estado === 'REASIGNADO' ? 'REASIGNADO' : 
        material.estado === 'AUDITADO' ? 'AUDITADO' : 
        material.estado === 'REVISADO' ? 'REVISADO' : null,
      descripcion_breve: values.descripcionBreve || null,
      descripcion_correcta: values.descripcionCorrecta || null,
      observacion: values.observacion || null,
      cambio_etiqueta: values.cambioEtiqueta === 'Sí' ? true : false || null,
      cambio_empaque: values.cambioEmpaque === 'Sí' ? true : false || null,
      cambio_descripcion:
        material.repuesto_descripcion !== values.descripcionCorrecta
          ? true
          : false || null,
      usuario_auditor_id: (finalizar && isAuditor ) ? idUser : null,
      comentario_auditor: values.comentarioAuditor || null,
      estado_ax: values.estadoDefinido || null,
      // fecha_auditoria: new Date(),
      fecha_auditoria: null,
      largo_mm: values.largo || null,
      ancho_mm: values.ancho || null,
      alto_mm: values.alto || null,
      diametro_mm: values.diametro || null,
      ...(tieneJsonMedidas
        ? {
            json_medidas: (() => {
              if (overrideJsonMedidas !== null && overrideJsonMedidas !== undefined) {
                if (Array.isArray(jsonMedidasEditable) || !jsonMedidasEditable?.especificaciones) {
                  return overrideJsonMedidas
                }
                return {
                  ...jsonMedidasEditable,
                  especificaciones: overrideJsonMedidas.map(m => ({
                    medida: m.medida ?? '',
                    descripcion: m.definicion_cotas ?? m.nombre ?? m.descripcion ?? '',
                    definicion_cotas: m.definicion_cotas ?? m.nombre ?? m.descripcion ?? '',
                    tipo_campo: m.tipo_campo ?? 'Num',
                    opciones: m.opciones ?? '',
                    valor: String(m.valor ?? ''),
                  })),
                }
              }
              if (Array.isArray(jsonMedidasEditable)) {
                return jsonMedidasEditable.length > 0 ? jsonMedidasEditable : (jsonMedidasFromMaterial ?? [])
              }
              return jsonMedidasEditable ?? jsonMedidasFromMaterial ?? []
            })(),
          }
        : {}),
      peso_g: values.peso || null,
      unidad_empaque_recomendada: values.unidadEmpaqueRecomendada || null,
      uso_aplicacion: values.usoAplicacion,
      composicion: values.composicion,
      especificaciones_producto: values.especificacionesProducto,
      especificaciones_electricas: values.especificacionesElectricas,
      tipo_empaque_sugerido_id: values.tipoEmpaque,
      tipos_proteccion_ids: values.proteccion || [],
      embalaje_sugerido_id: values.embalaje || null,
      marca_fabrica_id:
        values.marcaFabrica != null && values.marcaFabrica !== '' ? values.marcaFabrica : null,
      marca_sugerida_id: values.marca || null,
      categoria_sugerida_id: values.categoria || null,
      subcategoria_sugerida_id: values.subcategoria || null,
      segmento_sugerido_id: values.segmento || null,
      subsegmento_sugerido_id: values.subsegmento || null,
      descripcion_breve_ia: descripcionBreveIA || '',
      descripcion_breve_ia_modificada: descripcionBreveIA ? descripcionBreveIAModificada : 0,
      descripcion_correcta_urgente: descripcionUrgente ? 1 : 0,
      imagen_favorita: (() => {
        if (imagenFavorita) return imagenFavorita
        const conUrl = images.filter(img => img?.signed_url)
        return conUrl.length === 1 ? conUrl[0].nombre_objeto : null
      })(),
      requiere_actualizacion_catalogo: requiereActualizacionCatalogo,
      ...(medidasRecibidasPorSocket ? { es_mitutoyo: 1 } : {}),
    }
    const response = await putUpdateRevision(data, {
      codigo: material.repuesto_codigo_material_sap,
      descripcionNueva: values.descripcionCorrecta,
      descripcionAnterior: material.revision_descripcion_correcta,
      estado: material.estado,
    })
    if (response && response.success) {
      toast.success('Revisión actualizada correctamente')
      onResetData?.()
      if (finalizar) {
        // onClose?.()
      }
    } else {
      console.error('Error al actualizar la revisión')
    }
  }

  const uploadButton = (
    <div style={{ textAlign: 'center' }}>
      <UploadOutlined style={{ fontSize: 18, color: '#1890ff' }} />
      <div style={{ marginTop: 4, color: '#999', fontSize: '11px' }}>Subir</div>
    </div>
  )

  const liberarMaterial = async () => {
    const response = await putLiberarMaterial({
      repuestoId: material.repuesto_id,
    })
    if (response && response.success) {
      onResetData?.()
      toast.success('Material liberado correctamente')

      console.log(response)
    } else {
      console.error('Error al liberar material')
    }
    onClose?.()
  }

  // ==================== FUNCIONES: Gestión de Imágenes ====================
  const handleUploadReferenceImage = async (imageFile) => {
    const response = await uploadReferenceImage(
      material.repuesto_codigo_material_sap,
      imageFile,
    )
    if (response) {
      console.log(response.gs_url)
      handleGetImagesByMaterial()
    }
  }

  const handleDeleteImage = async (imageId) => {
    if (imagenFavorita === imageId) {
      const otra = images.find(img => img?.signed_url && img.nombre_objeto !== imageId)
      setImagenFavorita(otra ? otra.nombre_objeto : null)
    }
    const response = await deleteReferenceImage(imageId)
    if (response && response.success) {
      handleGetImagesByMaterial()
    }
  }

  const handleGetImagenesEmpaque = async () => {
    const response = await getImagenesEmpaque(material.repuesto_codigo_material_sap)
    const lista = Array.isArray(response?.success) ? response.success : Array.isArray(response) ? response : []
    setImagenesEmpaque(
      lista.map(img => ({
        ...img,
        nombre_objeto: img.nombre_objeto ?? img.path,
        signed_url: img.signed_url ?? img.url,
      })),
    )
  }

  const handleUploadImagenEmpaque = async (imageFile) => {
    const response = await uploadImagenEmpaque(
      material.repuesto_codigo_material_sap,
      imageFile,
    )
    if (response) {
      handleGetImagenesEmpaque()
    } else {
      toast.error('No se pudo subir la imagen de empaque')
    }
  }

  const handleDeleteImagenEmpaque = async (path) => {
    const response = await deleteImagenEmpaque(
      material.repuesto_codigo_material_sap,
      path,
    )
    if (response) {
      handleGetImagenesEmpaque()
    } else {
      toast.error('No se pudo eliminar la imagen de empaque')
    }
  }

  const handleGetImagenCotizacion = async () => {
    const response = await getImagenCotizacion(material.repuesto_codigo_material_sap)
    let imagen = response?.success ?? response?.data ?? response
    if (Array.isArray(imagen)) imagen = imagen[0]
    setImagenCotizacionGuardada(
      typeof imagen === 'string' ? imagen : imagen?.signed_url ?? imagen?.url ?? null,
    )
  }

  // Solo hay una imagen de cotización por material: subir otra la reemplaza.
  const handleGuardarImagenCotizacion = async (blob) => {
    const response = await uploadImagenCotizacion(
      material.repuesto_codigo_material_sap,
      blob,
    )
    if (!response) return false
    handleGetImagenCotizacion()
    return true
  }

  const handleDownloadImage = async (imageUrl, imageName) => {
    try {
      // Exportar siempre a 1000x1000 (contain + fondo blanco) para ecommerce.
      const blob = await exportarImagenEcommerce(imageUrl)
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = nombreArchivoEcommerce(imageName)
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    } catch (error) {
      console.error('Error downloading image:', error)
      toast.error('No se pudo exportar la imagen a 1000x1000')
      // Fallback: descarga original si falla el resize (p. ej. CORS).
      try {
        const link = document.createElement('a')
        link.href = imageUrl
        link.download = imageName || 'imagen.jpg'
        link.target = '_blank'
        link.rel = 'noopener noreferrer'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
      } catch (fallbackError) {
        console.error('Error en descarga fallback:', fallbackError)
      }
    }
  }

  // Abre el editor de la imagen para cotización. La imagen de referencia de la
  // plantilla se pide de nuevo porque las URLs firmadas guardadas pueden vencer.
  const handleAbrirImagenCotizacion = async () => {
    setImagenesPlantillaCotizacion([])
    setModalCotizacionOpen(true)
    setCargandoPlantillaCotizacion(true)
    try {
      const values = form.getFieldsValue()
      const categoriaId = values.categoria || material?.revision_categoria_sugerida_id
      const subcategoriaId = values.subcategoria || material?.revision_subcategoria_sugerida_id
      const definicionCotas = jsonMedidasFromMaterial?.definicion_cotas
      let gsUrl = jsonMedidasFromMaterial?.gs_url || null
      if (categoriaId && subcategoriaId && definicionCotas) {
        const raw = await getPlantillaMedidas(categoriaId, subcategoriaId, definicionCotas)
        const data = raw?.data ?? raw
        const urls = (Array.isArray(data?.imagenes_procesadas) ? data.imagenes_procesadas : [])
          .map(p => p.signed_url)
          .filter(Boolean)
        if (urls.length > 0) {
          setImagenesPlantillaCotizacion(urls)
          return
        }
        gsUrl = data?.gs_url || gsUrl
      }
      if (gsUrl) {
        const firmada = await postFirmarRevision(gsUrl)
        setImagenesPlantillaCotizacion(firmada?.url ? [firmada.url] : [])
      }
    } catch (error) {
      console.error('Error cargando la imagen de la plantilla:', error)
    } finally {
      setCargandoPlantillaCotizacion(false)
    }
  }

  const handleTomarFotoMock = () => {
    const gsurl = 'gs://axion-images-auteco/axion-b3/camara_2026-01-21_11-24-32.png'
    
    // Simular el proceso del socket: firmar y abrir modal de medidas
    postFirmarRevision(gsurl).then((response) => {
      console.log('📝 Resultado de firmar revisión:', response)
      if (response && response.url) {
        console.log('✅ Revisión firmada exitosamente')
        abrirModalMedidas(gsurl, response.url)
      } else {
        console.error('❌ Error al firmar revisión:', response)
        toast.error('Error al procesar la imagen')
      }
    }).catch((error) => {
      console.error('❌ Error al firmar revisión:', error)
      toast.error('Error al procesar la imagen')
    })
    
    handleGetImagesByMaterial()
  }

  const handleTomarFoto = (destino = 'material') => {
    if (!basculaConectada) {
      toast.warning('El socket no está conectado')
      return
    }
    destinoFotoRef.current = destino
    
    const mensaje = {
      type: 'CAMARA',
      data: 'tomar foto'
    }
    
    basculaWebSocketService.sendMessage(mensaje)
    console.log('📸 Comando de tomar foto enviado:', mensaje)
  }

  // Función para abrir el modal de medidas después de procesar la imagen
  const abrirModalMedidas = async (gsUriFinal, imagenUrlFinal) => {
    // Resetear medidas manuales antes de abrir el modal
    setMedidasManuales([])
    setContadorMedidasManuales(0)
    setCoordenadas([])
    setMedidasEditadas({})
    setNombresEditados({})
    setDescripcionesEditadas({})
    setModoDibujo(null)
    setPuntoA(null)
    setPuntoBTemporal(null)
    setPuntoBConfirmado(false)
    setCentroCirculo(null)
    setPuntoRadioTemporal(null)
    setRadioConfirmado(false)
    
    // NO resetear el gsurl recortado si ya existe (para mantener el recorte si ya se hizo)
    // setGsurlRecortado(null) // Comentado: mantener el recorte si existe
    
    // Guardar la URL y el gs_uri; abrir primero modal de edición/recorte (paso 1), luego quitar fondo (paso 2)
    setImagenFirmadaUrl(imagenUrlFinal)
    setImagenFirmadaBlob(null)
    setGsUriFirmado(gsUriFinal)
    setGsurlOriginal(gsUriFinal)
    setFlujoFotoPrimeroEdicion(true)
    setImagenParaEditar(imagenUrlFinal)
    setIsSelecting(false)
    setSelectionStart(null)
    setSelectionEnd(null)
    setRotacionAngulo(0)
    setModalEdicionVisible(true)
  }

  const handleContinuarImagen = async () => {
    const datos = {
      url: imagenFirmadaUrl,
      gs_uri: gsUriFirmado
    }
    console.log('📋 Datos de la imagen firmada:', JSON.stringify(datos, null, 2))
    
    try {
      // Usar la imagen recortada si existe, sino la original
      const gsUrlParaCotas = gsurlRecortado || gsurlOriginal
      // Enviar la imagen al endpoint para generar imagen con cotas (solo para el endpoint)
      const resultado = await postGenerarImagenConCotas(gsUrlParaCotas, form.getFieldValue('descripcionCorrecta')) // aqui se debe enviar la descripcion correcta
      console.log('📊 Respuesta de generar imagen con cotas:', resultado)
      
      if (resultado && resultado.url) {
        console.log('✅ Imagen con cotas generada exitosamente')
        // NO actualizar la imagen mostrada - mantener la imagen original
        // La imagen con cotas solo se usa para el endpoint de coordenadas
        
        // Llamar al endpoint de coordenadas usando la imagen con cotas
        if (resultado.nombre) {
          try {
            const coordenadasResultado = await getGenerarCoordenadas(resultado.nombre)
            console.log('📍 Resultado de generar coordenadas:', coordenadasResultado)
            
            if (coordenadasResultado && coordenadasResultado.coordenadas) {
              // Parsear el JSON string de coordenadas
              let coordenadasParseadas = JSON.parse(coordenadasResultado.coordenadas)
              
              // Si es un array, tomar el primer elemento
              if (Array.isArray(coordenadasParseadas) && coordenadasParseadas.length > 0) {
                coordenadasParseadas = coordenadasParseadas[0]
              }
              
              // Extraer las cotas del array
              if (coordenadasParseadas.inspeccion_tecnica && 
                  coordenadasParseadas.inspeccion_tecnica.cotas) {
                const {cotas} = coordenadasParseadas.inspeccion_tecnica
                setCoordenadas(cotas)
                // Guardar la unidad de medida
                const unidadMedidaRec = coordenadasParseadas.inspeccion_tecnica.unidad_medida_recomendada
                if (unidadMedidaRec) {
                  setUnidadMedida(unidadMedidaRec)
                }
                // Inicializar medidas editadas con valores vacíos
                const medidasIniciales = {}
                const nombresIniciales = {}
                const descripcionesIniciales = {}
                cotas.forEach((cota, index) => {
                  medidasIniciales[index] = ''
                  nombresIniciales[index] = cota.label || ''
                  descripcionesIniciales[index] = cota.descripcion_tecnica || ''
                })
                setMedidasEditadas(medidasIniciales)
                setNombresEditados(nombresIniciales)
                setDescripcionesEditadas(descripcionesIniciales)
              }
            }
          } catch (error) {
            console.error('❌ Error al llamar endpoint de coordenadas:', error)
            setCoordenadas([])
          }
        }
      } else {
        console.error('❌ Error al generar imagen con cotas')
      }
    } catch (error) {
      console.error('❌ Error al procesar la imagen:', error)
    }
  }

  const nombreDestinoFoto = () =>
    destinoFotoRef.current === 'empaque' ? 'Imagen de empaque' : 'Foto del material'

  // Sube la foto que salió del flujo de cámara al destino elegido al tomarla.
  const subirFotoCamara = async (file) => {
    if (destinoFotoRef.current === 'empaque') {
      await handleUploadImagenEmpaque(file)
    } else {
      await handleUploadReferenceImage(file)
    }
  }

  const handleGuardarFotoMaterial = async (imagenProcesadaBlob) => {
    try {
      if (imagenProcesadaBlob && imagenProcesadaBlob instanceof Blob) {
        // Imagen ya procesada (quitar fondo + colores) desde ModalMedidasCanvas → guardar en catálogo
        const file = new File([imagenProcesadaBlob], 'foto-material.jpg', { type: imagenProcesadaBlob.type || 'image/jpeg' })
        if (imagenFirmadaUrl && imagenFirmadaUrl.startsWith('blob:')) {
          URL.revokeObjectURL(imagenFirmadaUrl)
        }
        setModalImagenVisible(false)
        setImagenFirmadaUrl(null)
        setImagenFirmadaBlob(null)
        setGsUriFirmado(null)
        setGsurlRecortado(null)
        await subirFotoCamara(file)
        toast.success(`${nombreDestinoFoto()} guardada exitosamente`)
        return
      }
      if (!gsurlOriginal) {
        toast.error('No hay imagen original para guardar')
        return
      }
      const responseFirmada = await postFirmarRevision(gsurlOriginal)
      if (responseFirmada && responseFirmada.url) {
        setImagenParaEditar(responseFirmada.url)
        setModalEdicionVisible(true)
        setIsSelecting(false)
        setSelectionStart(null)
        setSelectionEnd(null)
        setRotacionAngulo(0)
      } else {
        toast.error('Error al cargar la imagen original')
      }
    } catch (error) {
      console.error('Error al firmar/cargar imagen:', error)
      toast.error('Error al cargar la imagen')
    }
  }

  // Función para abrir el modal de recorte
  const handleRecortar = async () => {
    if (!gsurlOriginal) {
      toast.error('No hay imagen original para recortar')
      return
    }
    
    // Siempre usar la imagen original para recortar
    try {
      const responseFirmada = await postFirmarRevision(gsurlOriginal)
      if (responseFirmada && responseFirmada.url) {
        // Guardar la URL original firmada para el modal de recorte
        setImagenOriginalParaRecorte(responseFirmada.url)
        setModalRecorteVisible(true)
      } else {
        toast.error('Error al cargar la imagen original para recortar')
      }
    } catch (error) {
      console.error('Error al firmar imagen original para recortar:', error)
      toast.error('Error al cargar la imagen original')
    }
  }

  // Función para confirmar el recorte
  const handleConfirmarRecorte = async (imagenBlob) => {
    if (!imagenBlob) {
      setModalRecorteVisible(false)
      return
    }

    setRecortando(true)
    try {
      // Obtener el id del material (repuesto_id)
      const materialId = material?.repuesto_id
      if (!materialId) {
        toast.error('No se encontró el ID del material')
        setRecortando(false)
        return
      }

      // Llamar al endpoint de recorte
      const resultado = await postImagenCotasRecortada(imagenBlob, materialId)
      console.log('📊 Resultado de recortar imagen:', resultado)
      if (resultado && resultado.gs_url) {
        // Guardar el gsurl recortado
        setGsurlRecortado(resultado.gs_url)
        
        // Firmar la imagen recortada para obtener la URL
        const responseFirmada = await postFirmarRevision(resultado.gs_url)
        if (responseFirmada && responseFirmada.url) {
          // Actualizar la imagen mostrada con la recortada
          setImagenFirmadaUrl(responseFirmada.url)
          setImagenFirmadaBlob(null)
          setGsUriFirmado(resultado.gs_url)
          toast.success('Imagen recortada exitosamente')
        } else {
          toast.error('Error al firmar la imagen recortada')
        }
      } else {
        toast.error('Error al recortar la imagen')
      }
    } catch (error) {
      console.error('Error al recortar imagen:', error)
      toast.error('Error al recortar la imagen')
    } finally {
      setRecortando(false)
      setModalRecorteVisible(false)
      setImagenOriginalParaRecorte(null)
    }
  }

  const handleMouseDownCrop = (e) => {
    if (!cropImageRef.current) return
    const rect = cropImageRef.current.getBoundingClientRect()
    const x = e.clientX - rect.left
    const y = e.clientY - rect.top
    setIsSelecting(true)
    setSelectionStart({ x, y })
    setSelectionEnd({ x, y })
  }

  const handleMouseMoveCrop = (e) => {
    if (!isSelecting || !cropImageRef.current) return
    const rect = cropImageRef.current.getBoundingClientRect()
    const x = e.clientX - rect.left
    const y = e.clientY - rect.top
    setSelectionEnd({ x, y })
  }

  const handleMouseUpCrop = () => {
    setIsSelecting(false)
    // Mantener la selección visible después de soltar el mouse
  }

  const handleAplicarRecorte = async () => {
    if (!imagenParaEditar) {
      toast.error('No hay imagen para guardar')
      return
    }

    // Si no hay selección válida, guardar la imagen sin recortar
    if (!selectionStart || !selectionEnd || !cropImageRef.current) {
      await guardarImagenFinalSinRecorte(imagenParaEditar)
      return
    }

    // Verificar que la selección tenga un tamaño mínimo
    const width = Math.abs(selectionEnd.x - selectionStart.x)
    const height = Math.abs(selectionEnd.y - selectionStart.y)
    if (width < 10 || height < 10) {
      // Si la selección es muy pequeña, guardar la imagen completa
      await guardarImagenFinalSinRecorte(imagenParaEditar)
      return
    }

    try {
      // Obtener la imagen como blob para evitar problemas de CORS
      const response = await fetch(imagenParaEditar)
      const blob = await response.blob()
      const imageUrl = URL.createObjectURL(blob)

      // Crear un nuevo objeto Image desde el blob (usar window.Image para evitar conflictos con antd)
      let img = new window.Image()
      img.crossOrigin = 'anonymous'
      
      await new Promise((resolve, reject) => {
        img.onload = resolve
        img.onerror = (e) => {
          URL.revokeObjectURL(imageUrl)
          reject(new Error('Error al cargar la imagen'))
        }
        img.src = imageUrl
      })

      // Obtener las dimensiones de la imagen mostrada
      const imgElement = cropImageRef.current
      if (!imgElement) {
        URL.revokeObjectURL(imageUrl)
        throw new Error('No se encontró el elemento de imagen')
      }

      // Primero rotar la imagen completa si es necesario
      if (rotacionAngulo !== 0) {
        const anguloRad = (rotacionAngulo * Math.PI) / 180
        const cos = Math.abs(Math.cos(anguloRad))
        const sin = Math.abs(Math.sin(anguloRad))
        
        const newWidth = img.width * cos + img.height * sin
        const newHeight = img.width * sin + img.height * cos
        
        const canvasRotado = document.createElement('canvas')
        canvasRotado.width = newWidth
        canvasRotado.height = newHeight
        const ctxRotado = canvasRotado.getContext('2d')
        
        if (!ctxRotado) {
          URL.revokeObjectURL(imageUrl)
          throw new Error('No se pudo obtener el contexto del canvas')
        }
        
        ctxRotado.translate(newWidth / 2, newHeight / 2)
        ctxRotado.rotate(anguloRad)
        ctxRotado.drawImage(img, -img.width / 2, -img.height / 2)
        
        // Crear nueva imagen desde el canvas rotado
        const imgRotada = new window.Image()
        imgRotada.src = canvasRotado.toDataURL('image/jpeg', 0.9)
        await new Promise((resolve) => {
          imgRotada.onload = resolve
        })
        img = imgRotada
      }

      // La imagen (img) ya fue rotada a su orientación visual (canvasRotado) cuando
      // rotacionAngulo !== 0, por lo que su bounding box en pantalla corresponde 1:1
      // (con escala uniforme) a sus píxeles. No hay que remapear coordenadas por ángulo.
      const rect = imgElement.getBoundingClientRect()
      const scaleX = img.width / rect.width
      const scaleY = img.height / rect.height

      const startX = Math.min(selectionStart.x, selectionEnd.x)
      const startY = Math.min(selectionStart.y, selectionEnd.y)
      const endX = Math.max(selectionStart.x, selectionEnd.x)
      const endY = Math.max(selectionStart.y, selectionEnd.y)

      const x = startX * scaleX
      const y = startY * scaleY
      const cropWidth = (endX - startX) * scaleX
      const cropHeight = (endY - startY) * scaleY

      // Crear canvas para el recorte
      const canvas = document.createElement('canvas')
      canvas.width = cropWidth
      canvas.height = cropHeight
      const ctx = canvas.getContext('2d')

      if (!ctx) {
        URL.revokeObjectURL(imageUrl)
        throw new Error('No se pudo obtener el contexto del canvas')
      }

      // Dibujar la porción recortada desde el objeto Image (que viene del blob)
      ctx.drawImage(
        img,
        x, y, cropWidth, cropHeight, // origen en la imagen
        0, 0, cropWidth, cropHeight  // destino en el canvas
      )

      // Ahora podemos usar toDataURL porque la imagen viene de un blob local
      const croppedDataUrl = canvas.toDataURL('image/jpeg', 0.9)
      
      // Limpiar el objeto URL
      URL.revokeObjectURL(imageUrl)
      
      await guardarImagenFinal(croppedDataUrl)
    } catch (error) {
      console.error('Error al aplicar recorte:', error)
      toast.error(`Error al aplicar recorte: ${error.message || 'Error desconocido'}`)
    }
  }

  // Obtiene la imagen actual (con recorte y rotación aplicados) como Blob, para el flujo "Continuar" → quitar fondo
  const obtenerImagenActualParaContinuar = async () => {
    const dataURLtoBlob = (dataurl) => {
      const arr = dataurl.split(',')
      const mime = arr[0].match(/:(.*?);/)[1]
      const bstr = atob(arr[1])
      const n = bstr.length
      const u8arr = new Uint8Array(n)
      for (let i = 0; i < n; i++) u8arr[i] = bstr.charCodeAt(i)
      return new Blob([u8arr], { type: mime })
    }
    if (!imagenParaEditar) return null
    const sinRecorteValido = !selectionStart || !selectionEnd || !cropImageRef.current
    const width = selectionStart && selectionEnd ? Math.abs(selectionEnd.x - selectionStart.x) : 0
    const height = selectionStart && selectionEnd ? Math.abs(selectionEnd.y - selectionStart.y) : 0
    const seleccionMinima = width >= 10 && height >= 10
    if (sinRecorteValido || !seleccionMinima) {
      // Imagen completa, con rotación si hay
      if (rotacionAngulo !== 0) {
        const response = await fetch(imagenParaEditar)
        const blob = await response.blob()
        const imageUrlLocal = URL.createObjectURL(blob)
        const img = new window.Image()
        await new Promise((resolve, reject) => {
          img.onload = resolve
          img.onerror = reject
          img.src = imageUrlLocal
        })
        const anguloRad = (rotacionAngulo * Math.PI) / 180
        const cos = Math.abs(Math.cos(anguloRad))
        const sin = Math.abs(Math.sin(anguloRad))
        const newWidth = img.width * cos + img.height * sin
        const newHeight = img.width * sin + img.height * cos
        const canvas = document.createElement('canvas')
        canvas.width = newWidth
        canvas.height = newHeight
        const ctx = canvas.getContext('2d')
        if (!ctx) {
          URL.revokeObjectURL(imageUrlLocal)
          return null
        }
        ctx.translate(newWidth / 2, newHeight / 2)
        ctx.rotate(anguloRad)
        ctx.drawImage(img, -img.width / 2, -img.height / 2)
        const rotatedDataUrl = canvas.toDataURL('image/jpeg', 0.9)
        URL.revokeObjectURL(imageUrlLocal)
        return dataURLtoBlob(rotatedDataUrl)
      }
      const response = await fetch(imagenParaEditar)
      return response.blob()
    }
    try {
      const response = await fetch(imagenParaEditar)
      const blob = await response.blob()
      const imageUrl = URL.createObjectURL(blob)
      const img = new window.Image()
      img.crossOrigin = 'anonymous'
      await new Promise((resolve, reject) => {
        img.onload = resolve
        img.onerror = () => {
          URL.revokeObjectURL(imageUrl)
          reject(new Error('Error al cargar la imagen'))
        }
        img.src = imageUrl
      })
      const imgElement = cropImageRef.current
      if (!imgElement) {
        URL.revokeObjectURL(imageUrl)
        return null
      }
      let imgToCrop = img
      if (rotacionAngulo !== 0) {
        const anguloRad = (rotacionAngulo * Math.PI) / 180
        const cos = Math.abs(Math.cos(anguloRad))
        const sin = Math.abs(Math.sin(anguloRad))
        const newWidth = img.width * cos + img.height * sin
        const newHeight = img.width * sin + img.height * cos
        const canvasRotado = document.createElement('canvas')
        canvasRotado.width = newWidth
        canvasRotado.height = newHeight
        const ctxRotado = canvasRotado.getContext('2d')
        if (!ctxRotado) {
          URL.revokeObjectURL(imageUrl)
          return null
        }
        ctxRotado.translate(newWidth / 2, newHeight / 2)
        ctxRotado.rotate(anguloRad)
        ctxRotado.drawImage(img, -img.width / 2, -img.height / 2)
        const imgRotada = new window.Image()
        imgRotada.src = canvasRotado.toDataURL('image/jpeg', 0.9)
        await new Promise((resolve) => { imgRotada.onload = resolve })
        imgToCrop = imgRotada
      }
      // imgToCrop ya está en su orientación visual (rotada si aplica), por lo que su
      // bounding box en pantalla mapea 1:1 (con escala uniforme) a sus píxeles.
      // No hay que remapear coordenadas por ángulo.
      const rect = imgElement.getBoundingClientRect()
      const scaleX = imgToCrop.width / rect.width
      const scaleY = imgToCrop.height / rect.height
      const startX = Math.min(selectionStart.x, selectionEnd.x)
      const startY = Math.min(selectionStart.y, selectionEnd.y)
      const endX = Math.max(selectionStart.x, selectionEnd.x)
      const endY = Math.max(selectionStart.y, selectionEnd.y)
      const x = startX * scaleX
      const y = startY * scaleY
      const cropWidth = (endX - startX) * scaleX
      const cropHeight = (endY - startY) * scaleY
      const canvas = document.createElement('canvas')
      canvas.width = cropWidth
      canvas.height = cropHeight
      const ctx = canvas.getContext('2d')
      if (!ctx) {
        URL.revokeObjectURL(imageUrl)
        return null
      }
      ctx.drawImage(imgToCrop, x, y, cropWidth, cropHeight, 0, 0, cropWidth, cropHeight)
      const croppedDataUrl = canvas.toDataURL('image/jpeg', 0.9)
      URL.revokeObjectURL(imageUrl)
      return dataURLtoBlob(croppedDataUrl)
    } catch (err) {
      console.error('Error al obtener imagen para continuar:', err)
      return null
    }
  }

  const handleContinuarDesdeEdicion = async () => {
    if (!imagenParaEditar) {
      toast.error('No hay imagen para continuar')
      return
    }
    try {
      const blob = await obtenerImagenActualParaContinuar()
      if (!blob) {
        toast.error('No se pudo obtener la imagen')
        return
      }
      const urlDesdeEdicion = URL.createObjectURL(blob)
      setImagenFirmadaUrl(urlDesdeEdicion)
      setImagenFirmadaBlob(blob)
      setFlujoFotoPrimeroEdicion(false)
      setModalEdicionVisible(false)
      setImagenParaEditar(null)
      setIsSelecting(false)
      setSelectionStart(null)
      setSelectionEnd(null)
      setRotacionAngulo(0)
      setModalImagenVisible(true)
    } catch (error) {
      console.error('Error al continuar desde edición:', error)
      toast.error('Error al preparar la imagen para quitar fondo')
    }
  }

  const guardarImagenFinalSinRecorte = async (imageUrl) => {
    try {
      // Si hay rotación, aplicar la rotación antes de guardar
      if (rotacionAngulo !== 0) {
        const response = await fetch(imageUrl)
        const blob = await response.blob()
        const imageUrlLocal = URL.createObjectURL(blob)
        
        const img = new window.Image()
        await new Promise((resolve, reject) => {
          img.onload = resolve
          img.onerror = reject
          img.src = imageUrlLocal
        })

        const anguloRad = (rotacionAngulo * Math.PI) / 180
        const cos = Math.abs(Math.cos(anguloRad))
        const sin = Math.abs(Math.sin(anguloRad))
        
        const newWidth = img.width * cos + img.height * sin
        const newHeight = img.width * sin + img.height * cos
        
        const canvas = document.createElement('canvas')
        canvas.width = newWidth
        canvas.height = newHeight
        const ctx = canvas.getContext('2d')
        
        if (!ctx) {
          URL.revokeObjectURL(imageUrlLocal)
          throw new Error('No se pudo obtener el contexto del canvas')
        }
        
        ctx.translate(newWidth / 2, newHeight / 2)
        ctx.rotate(anguloRad)
        ctx.drawImage(img, -img.width / 2, -img.height / 2)
        
        const rotatedDataUrl = canvas.toDataURL('image/jpeg', 0.9)
        URL.revokeObjectURL(imageUrlLocal)
        
        // Convertir data URL a File
        const dataURLtoFile = (dataurl, filename) => {
          const arr = dataurl.split(',')
          const mime = arr[0].match(/:(.*?);/)[1]
          const bstr = atob(arr[1])
          let n = bstr.length
          const u8arr = new Uint8Array(n)
          while (n--) {
            u8arr[n] = bstr.charCodeAt(n)
          }
          return new File([u8arr], filename, { type: mime })
        }
        
        const file = dataURLtoFile(rotatedDataUrl, 'foto-material.jpg')
        await subirFotoCamara(file)
      } else {
        // Sin rotación, guardar directamente
        const response = await fetch(imageUrl)
        const blob = await response.blob()
        const file = new File([blob], 'foto-material.jpg', { type: blob.type || 'image/jpeg' })
        await subirFotoCamara(file)
      }
      
      toast.success(`${nombreDestinoFoto()} guardada exitosamente`)
      setModalEdicionVisible(false)
      setImagenParaEditar(null)
      setIsSelecting(false)
      setSelectionStart(null)
      setSelectionEnd(null)
      setRotacionAngulo(0)
    } catch (error) {
      console.error('Error al guardar la foto del material:', error)
      toast.error(`Error al guardar: ${error.message || 'Error desconocido'}`)
    }
  }

  const guardarImagenFinal = async (imageDataUrl) => {
    try {
      // Convertir el data URL a un File usando una función auxiliar
      const dataURLtoFile = (dataurl, filename) => {
        const arr = dataurl.split(',')
        const mime = arr[0].match(/:(.*?);/)[1]
        const bstr = atob(arr[1])
        let n = bstr.length
        const u8arr = new Uint8Array(n)
        while (n--) {
          u8arr[n] = bstr.charCodeAt(n)
        }
        return new File([u8arr], filename, { type: mime })
      }
      
      // Crear un File desde el data URL
      const file = dataURLtoFile(imageDataUrl, 'foto-material.jpg')
      
      // Usar la función existente para subir la imagen
      await subirFotoCamara(file)
      
      toast.success(`${nombreDestinoFoto()} guardada exitosamente`)
      setModalEdicionVisible(false)
      setImagenParaEditar(null)
      setIsSelecting(false)
      setSelectionStart(null)
      setSelectionEnd(null)
      setRotacionAngulo(0)
    } catch (error) {
      console.error('Error al guardar la foto del material:', error)
      toast.error(`Error al guardar: ${error.message || 'Error desconocido'}`)
    }
  }

  const handleCancelarEdicion = () => {
    setModalEdicionVisible(false)
    setImagenParaEditar(null)
    setFlujoFotoPrimeroEdicion(false)
    setIsSelecting(false)
    setSelectionStart(null)
    setSelectionEnd(null)
    setRotacionAngulo(0)
  }

  const handleLimpiarSeleccion = () => {
    setIsSelecting(false)
    setSelectionStart(null)
    setSelectionEnd(null)
  }

  const handleRotarIzquierda = () => {
    setRotacionAngulo((prev) => (prev - 90) % 360)
    // Limpiar selección al rotar
    setIsSelecting(false)
    setSelectionStart(null)
    setSelectionEnd(null)
  }

  const handleRotarDerecha = () => {
    setRotacionAngulo((prev) => (prev + 90) % 360)
    // Limpiar selección al rotar
    setIsSelecting(false)
    setSelectionStart(null)
    setSelectionEnd(null)
  }

  const handleCambiarMedida = (index, valor) => {
    setMedidasEditadas(prev => ({
      ...prev,
      [index]: valor
    }))
  }

  // ==================== FUNCIONES: Canvas - Modo Dibujo ====================
  const handleActivarDibujoLinea = () => {
    if (modoDibujo === null || modoDibujo === 'circuloCentro' || modoDibujo === 'circuloRadio') {
      // Activar modo dibujo línea
      setModoDibujo('puntoA')
      setPuntoA(null)
      setPuntoBTemporal(null)
      setPuntoBConfirmado(false)
      setCentroCirculo(null)
      setPuntoRadioTemporal(null)
      setRadioConfirmado(false)
    } else {
      // Cancelar modo dibujo
      setModoDibujo(null)
      setPuntoA(null)
      setPuntoBTemporal(null)
      setPuntoBConfirmado(false)
      setCentroCirculo(null)
      setPuntoRadioTemporal(null)
      setRadioConfirmado(false)
    }
  }

  const handleActivarDibujoCirculo = () => {
    if (modoDibujo === null || modoDibujo === 'puntoA' || modoDibujo === 'puntoB') {
      // Activar modo dibujo círculo
      setModoDibujo('circuloCentro')
      setCentroCirculo(null)
      setPuntoRadioTemporal(null)
      setRadioConfirmado(false)
      setPuntoA(null)
      setPuntoBTemporal(null)
      setPuntoBConfirmado(false)
    } else {
      // Cancelar modo dibujo
      setModoDibujo(null)
      setCentroCirculo(null)
      setPuntoRadioTemporal(null)
      setRadioConfirmado(false)
      setPuntoA(null)
      setPuntoBTemporal(null)
      setPuntoBConfirmado(false)
    }
  }


  // Hook para interacciones del canvas
  const {
    handleMouseMoveCanvas,
    handleMouseDownCanvas,
    handleMouseUpCanvas,
    handleClickCanvas,
    handleMouseMoveCanvasDrag,
  } = useCanvasInteractions({
    canvasRef,
    coordenadas,
    medidasManuales,
    medidaSeleccionada,
    lineaReferenciaSeleccionada,
    isDragging,
    arrastrandoPunto,
    modoDibujo,
    medidaIndexParaLineaReferencia,
    puntoALineaReferencia,
    puntoBLineaReferenciaConfirmado,
    puntoA,
    puntoBConfirmado,
    centroCirculo,
    radioConfirmado,
    setMedidaSeleccionada,
    setLineaReferenciaSeleccionada,
    setArrastrandoPunto,
    setIsDragging,
    setCursorHover,
    setPuntoA,
    setPuntoBTemporal,
    setPuntoBConfirmado,
    setCentroCirculo,
    setPuntoRadioTemporal,
    setRadioConfirmado,
    setPuntoALineaReferencia,
    setPuntoBLineaReferencia,
    setPuntoBLineaReferenciaConfirmado,
    setMedidaIndexParaLineaReferencia,
    setModoDibujo,
    setCoordenadas,
    setMedidasManuales,
    inputRefs,
  })

  // ==================== FUNCIONES: Canvas - Medidas Manuales ====================
  const handleConfirmarMedidaManual = () => {
    // Verificar si es una línea o un círculo
    const esLinea = puntoA && puntoBTemporal
    const esCirculo = centroCirculo && puntoRadioTemporal

    if (!esLinea && !esCirculo) return

    // Calcular la siguiente letra disponible consecutivamente
    const letraDisponible = calcularSiguienteLetraDisponible(coordenadas, medidasManuales)
    if (!letraDisponible) {
      toast.error('No hay más letras disponibles (A-Z ya están en uso)')
      return
    }

    let nuevaMedida

    if (esLinea) {
      // Crear medida de línea - usar solo la letra como label
      nuevaMedida = {
        label: letraDisponible,
        descripcion_tecnica: 'Medida manual',
        prioridad: 0,
        referencia_manual: {
          etiqueta_estandar: letraDisponible,
          definicion: 'Medida manual'
        },
        coordenadas_normalizadas: {
          x_ini: puntoA.x,
          y_ini: puntoA.y,
          x_fin: puntoBTemporal.x,
          y_fin: puntoBTemporal.y
        },
        es_manual: true
      }
    } else {
      // Crear medida de círculo
      // Calcular el radio como la distancia entre el centro y el punto temporal
      const dx = puntoRadioTemporal.x - centroCirculo.x
      const dy = puntoRadioTemporal.y - centroCirculo.y
      const radio = Math.sqrt(dx * dx + dy * dy)

      nuevaMedida = {
        label: letraDisponible,
        descripcion_tecnica: 'Medida manual',
        prioridad: 0,
        referencia_manual: {
          etiqueta_estandar: letraDisponible,
          definicion: 'Medida manual'
        },
        coordenadas_normalizadas: {
          x: centroCirculo.x,
          y: centroCirculo.y,
          radio
        },
        es_manual: true
      }
    }
    
    // Calcular coordenadas_etiqueta automáticamente
    const etiquetaCalculada = calcularCoordenadasEtiqueta(nuevaMedida)
    if (etiquetaCalculada) {
      nuevaMedida.coordenadas_etiqueta = etiquetaCalculada
    }

    setMedidasManuales(prev => [...prev, nuevaMedida])
    setContadorMedidasManuales(prev => prev + 1)
    
    // Inicializar medida editada vacía y estados locales
    const nuevoIndex = coordenadas.length + medidasManuales.length
    setMedidasEditadas(prev => ({
      ...prev,
      [nuevoIndex]: ''
    }))
    setNombresEditados(prev => ({
      ...prev,
      [nuevoIndex]: nuevaMedida.label
    }))
    setDescripcionesEditadas(prev => ({
      ...prev,
      [nuevoIndex]: nuevaMedida.descripcion_tecnica
    }))

    // Resetear modo dibujo
    setModoDibujo(null)
    setPuntoA(null)
    setPuntoBTemporal(null)
    setPuntoBConfirmado(false)
    setCentroCirculo(null)
    setPuntoRadioTemporal(null)
    setRadioConfirmado(false)
  }

  // ==================== FUNCIONES: Cotas Guardadas ====================
  const handleGuardarMedidas = () => {
    const todasLasCoordenadas = [...coordenadas, ...medidasManuales]
    const medidasTomadas = todasLasCoordenadas.map((cota, index) => ({
      label: cota.label,
      medida: medidasEditadas[index] || '',
      descripcion_tecnica: cota.descripcion_tecnica,
      prioridad: cota.prioridad
    }))

    const jsonGuardar = {
      gsurl_cotas: gsUriFirmado,
      medidas_tomadas: medidasTomadas
    }

    console.log('📋 JSON para guardar:', JSON.stringify(jsonGuardar, null, 2))
  }

  // Función para cargar y mostrar una medida específica
  const cargarMedidaSeleccionada = async (cota) => {
    if (!cota) return

    try {
      // Firmar la gs_url
      const response = await postFirmarRevision(cota.gs_url)
      if (response && response.url) {
        setImagenCotasUrl(response.url)
        
        // Parsear las coordenadas y medidas (vienen como strings JSON)
        let coordenadasParseadas = []
        let medidasParseadas = []
        
        if (cota.coordenadas) {
          try {
            const coordenadasStr = typeof cota.coordenadas === 'string' 
              ? cota.coordenadas 
              : JSON.stringify(cota.coordenadas)
            const coordenadasTemp = JSON.parse(coordenadasStr)
            
            // Si es un array de cotas directamente (formato guardado)
            if (Array.isArray(coordenadasTemp) && coordenadasTemp.length > 0) {
              // Verificar si el primer elemento tiene la estructura de cota guardada
              const primerElemento = coordenadasTemp[0]
              if (primerElemento.titulo || primerElemento.coordenada || primerElemento.geometria_cota) {
                // Es un array de cotas guardadas directamente - mantener TODO el array
                coordenadasParseadas = coordenadasTemp
              } else if (primerElemento.inspeccion_tecnica && primerElemento.inspeccion_tecnica.cotas) {
                // Es un array con objetos que tienen inspeccion_tecnica, tomar el primero
                coordenadasParseadas = primerElemento.inspeccion_tecnica.cotas
              } else {
                coordenadasParseadas = coordenadasTemp
              }
            } else if (coordenadasTemp && !Array.isArray(coordenadasTemp)) {
              // Si es un objeto único
              if (coordenadasTemp.inspeccion_tecnica && coordenadasTemp.inspeccion_tecnica.cotas) {
                // Si el objeto tiene inspeccion_tecnica.cotas, extraer las cotas
                coordenadasParseadas = coordenadasTemp.inspeccion_tecnica.cotas
              } else if (coordenadasTemp.titulo || coordenadasTemp.coordenada || coordenadasTemp.geometria_cota) {
                // Si es un objeto de cota guardada directamente
                coordenadasParseadas = [coordenadasTemp]
              } else {
                // Si es un objeto con cotas directamente
                coordenadasParseadas = [coordenadasTemp]
              }
            }
          } catch (e) {
            console.error('Error al parsear coordenadas:', e)
            coordenadasParseadas = []
          }
        }
        
        if (cota.medidas) {
          try {
            const medidasStr = typeof cota.medidas === 'string' 
              ? cota.medidas 
              : JSON.stringify(cota.medidas)
            medidasParseadas = JSON.parse(medidasStr)
          } catch (e) {
            console.error('Error al parsear medidas:', e)
            medidasParseadas = []
          }
        }
        
        // Asegurarse de que las coordenadas parseadas preserven geometria_cota y lineas_a_repuesto
        // Si las coordenadas vienen del endpoint, ya deberían tener estos campos
        // Pero verificamos que se preserven correctamente
        console.log('📍 Coordenadas parseadas:', coordenadasParseadas)
        console.log('📍 Primer elemento con referencias:', coordenadasParseadas[0]?.geometria_cota, coordenadasParseadas[0]?.lineas_a_repuesto)
        console.log('📍 Todas las cotas tienen geometria_cota?', coordenadasParseadas.map(c => ({
          titulo: c.titulo,
          tieneGeometria: !!c.geometria_cota,
          tieneLineas: !!c.lineas_a_repuesto,
          geometria: c.geometria_cota
        })))
        
        // Normalizar las coordenadas: convertir "coordenada" a "geometria_cota" si es necesario
        const coordenadasNormalizadas = coordenadasParseadas.map(cota => {
          // Si tiene "coordenada" pero no "geometria_cota", convertir el formato
          if (cota.coordenada && !cota.geometria_cota) {
            const coords = cota.coordenada
            if (coords.x_ini !== undefined && coords.y_ini !== undefined && 
                coords.x_fin !== undefined && coords.y_fin !== undefined) {
              return {
                ...cota,
                geometria_cota: {
                  x_ini: coords.x_ini,
                  y_ini: coords.y_ini,
                  x_fin: coords.x_fin,
                  y_fin: coords.y_fin
                }
              }
            }
          }
          return cota
        })
        
        // Calcular coordenadas_etiqueta para cada cota si no existe
        const coordenadasConEtiquetas = coordenadasNormalizadas.map(cota => {
          if (!cota.coordenadas_etiqueta) {
            const etiquetaCalculada = calcularCoordenadasEtiqueta(cota)
            if (etiquetaCalculada) {
              return {
                ...cota,
                coordenadas_etiqueta: etiquetaCalculada
              }
            }
          }
          return cota
        })
        
        // Actualizar tanto el estado como el ref
        coordenadasCotasRef.current = coordenadasConEtiquetas
        setCoordenadasCotas(coordenadasConEtiquetas)
        setMedidasCotas(medidasParseadas)
      } else {
        toast.error('Error al firmar la URL de la imagen')
      }
    } catch (error) {
      console.error('Error al cargar medida:', error)
      toast.error('Error al cargar la medida seleccionada')
    }
  }

  // Función para abrir el modal de visualización de cotas guardadas
  const handleVerCotasGuardadas = async () => {
    if (todasLasCotasGuardadas.length === 0) {
      toast.error('No hay cotas guardadas para mostrar')
      return
    }

    // Si hay una cota seleccionada, cargarla; si no, cargar la primera (última por id)
    const cotaACargar = cotaSeleccionadaId 
      ? todasLasCotasGuardadas.find(c => c.id === cotaSeleccionadaId) 
      : todasLasCotasGuardadas[0]
    
    if (!cotaACargar) {
      toast.error('No se encontró la medida seleccionada')
      return
    }

    await cargarMedidaSeleccionada(cotaACargar)
    setModalCotasVisible(true)
  }

  // Función para cambiar la medida seleccionada
  const handleCambiarMedidaSeleccionada = async (cotaId) => {
    const cotaSeleccionada = todasLasCotasGuardadas.find(c => c.id === cotaId)
    if (cotaSeleccionada) {
      setCotaSeleccionadaId(cotaId)
      await cargarMedidaSeleccionada(cotaSeleccionada)
    }
  }

  // Función para encontrar qué línea de referencia específica está cerca del punto clickeado
  // Función para encontrar qué línea de referencia está cerca del punto clickeado en el canvas de cotas
  const encontrarLineaReferenciaCercana = (x, y, margen = 5) => {
    const canvas = canvasCotasRef.current
    if (!canvas) return null

    const escalaX = canvas.width / 1000
    const escalaY = canvas.height / 1000
    const coordenadasActuales = coordenadasCotasRef.current

    if (!coordenadasActuales || coordenadasActuales.length === 0) {
      return null
    }

    for (let cotaIndex = 0; cotaIndex < coordenadasActuales.length; cotaIndex++) {
      const cota = coordenadasActuales[cotaIndex]
      
      // Buscar en lineas_a_repuesto (líneas de referencia rojas)
      if (cota.lineas_a_repuesto && Array.isArray(cota.lineas_a_repuesto)) {
        for (const linea of cota.lineas_a_repuesto) {
          if (linea && linea.x_ini !== undefined && linea.y_ini !== undefined && 
              linea.x_fin !== undefined && linea.y_fin !== undefined) {
            const x1 = linea.x_ini * escalaX
            const y1 = linea.y_ini * escalaY
            const x2 = linea.x_fin * escalaX
            const y2 = linea.y_fin * escalaY
            
            const distancia = distanciaPuntoALinea(x, y, x1, y1, x2, y2)
            if (distancia <= margen) {
              return cotaIndex // Retornar el índice de la cota
            }
          }
        }
      }
      
      // Buscar en geometria_cota (medidas moradas)
      if (cota.geometria_cota) {
        // Si es un objeto
        if (!Array.isArray(cota.geometria_cota)) {
          const geometria = cota.geometria_cota
          if (geometria.x_ini !== undefined && geometria.y_ini !== undefined && 
              geometria.x_fin !== undefined && geometria.y_fin !== undefined) {
            const x1 = geometria.x_ini * escalaX
            const y1 = geometria.y_ini * escalaY
            const x2 = geometria.x_fin * escalaX
            const y2 = geometria.y_fin * escalaY
            
            const distancia = distanciaPuntoALinea(x, y, x1, y1, x2, y2)
            if (distancia <= margen) {
              return cotaIndex
            }
          }
        }
        // Si es un array
        else {
          for (const geometria of cota.geometria_cota) {
            if (geometria && geometria.x_ini !== undefined && geometria.y_ini !== undefined && 
                geometria.x_fin !== undefined && geometria.y_fin !== undefined) {
              const x1 = geometria.x_ini * escalaX
              const y1 = geometria.y_ini * escalaY
              const x2 = geometria.x_fin * escalaX
              const y2 = geometria.y_fin * escalaY
              
              const distancia = distanciaPuntoALinea(x, y, x1, y1, x2, y2)
              if (distancia <= margen) {
                return cotaIndex
              }
            }
          }
        }
      }
      
      // Mantener compatibilidad con coordenada
      if (cota.coordenada) {
        const coords = cota.coordenada
        if (coords.x_ini !== undefined && coords.y_ini !== undefined && 
            coords.x_fin !== undefined && coords.y_fin !== undefined) {
          const x1 = coords.x_ini * escalaX
          const y1 = coords.y_ini * escalaY
          const x2 = coords.x_fin * escalaX
          const y2 = coords.y_fin * escalaY
          
          const distancia = distanciaPuntoALinea(x, y, x1, y1, x2, y2)
          if (distancia <= margen) {
            return cotaIndex
          }
        }
      }
    }

    return null
  }

  // Manejador de movimiento del mouse en el canvas de cotas
  const handleMouseMoveCanvasCotas = (e) => {
    const canvas = canvasCotasRef.current
    if (!canvas) return

    const rect = canvas.getBoundingClientRect()
    const x = e.clientX - rect.left
    const y = e.clientY - rect.top

    const cotaIndex = encontrarLineaReferenciaCercana(x, y, 5)
    
    if (cotaIndex !== null) {
      canvas.style.cursor = 'pointer'
    } else {
      canvas.style.cursor = 'default'
    }
  }

  // Manejador de clicks en el canvas de cotas
  const handleClickCanvasCotas = (e) => {
    const canvas = canvasCotasRef.current
    if (!canvas) return

    const rect = canvas.getBoundingClientRect()
    const x = e.clientX - rect.left
    const y = e.clientY - rect.top

    const cotaIndex = encontrarLineaReferenciaCercana(x, y, 5)
    
    if (cotaIndex !== null) {
      // Si el modal de imagen está abierto, buscar el input correspondiente
      if (modalImagenVisible) {
        const inputRef = inputRefs.current[cotaIndex]
        if (inputRef) {
          inputRef.scrollIntoView({ behavior: 'smooth', block: 'center' })
          setTimeout(() => {
            inputRef.focus()
          }, 300)
        }
      } else {
        // Si estamos en el modal de cotas guardadas, hacer scroll al panel de medidas
        const panelMedidas = document.querySelector('[data-panel-medidas]')
        if (panelMedidas) {
          // Buscar el elemento de la medida correspondiente
          const medidaElement = panelMedidas.querySelector(`[data-medida-index="${cotaIndex}"]`)
          if (medidaElement) {
            medidaElement.scrollIntoView({ behavior: 'smooth', block: 'center' })
            // Resaltar temporalmente la medida
            medidaElement.style.backgroundColor = '#fffbe6'
            setTimeout(() => {
              medidaElement.style.backgroundColor = ''
            }, 2000)
          } else {
            panelMedidas.scrollIntoView({ behavior: 'smooth', block: 'center' })
          }
        }
      }
    }
  }

  // Función para eliminar las medidas guardadas
  const handleEliminarMedidas = async () => {
    if (!cotaSeleccionadaId) {
      toast.error('No hay medidas para eliminar')
      return
    }

    const cotaAEliminar = todasLasCotasGuardadas.find(c => c.id === cotaSeleccionadaId)
    if (!cotaAEliminar) {
      toast.error('No se encontró la medida a eliminar')
      return
    }

    confirm({
      title: '¿Está seguro de eliminar las medidas?',
      content: `Esta acción eliminará la medida #${cotaSeleccionadaId} y no se puede deshacer.`,
      okText: 'Eliminar',
      okType: 'danger',
      cancelText: 'Cancelar',
      onOk: async () => {
        try {
          const imagenCotasId = cotaAEliminar.id || cotaAEliminar.imagen_cotas_id
          const gsUrl = cotaAEliminar.gs_url

          if (!imagenCotasId || !gsUrl) {
            toast.error('Error: Faltan datos necesarios para eliminar las medidas')
            return
          }

          const response = await deleteImagenCotas(imagenCotasId, gsUrl)
          
          if (response && (response.success !== false)) {
            toast.success('Medidas eliminadas exitosamente')
            
            // Recargar las cotas guardadas para actualizar el estado
            if (material?.revision_id) {
              const responseCotas = await getImagenCotas(material.revision_id)
              if (responseCotas && responseCotas.success && responseCotas.success.length > 0) {
                // Guardar todas las cotas ordenadas por id descendente
                const cotasOrdenadas = responseCotas.success.sort((a, b) => b.id - a.id)
                setTodasLasCotasGuardadas(cotasOrdenadas)
                // Obtener el último item (mayor id) para mantener compatibilidad
                const ultimaCota = cotasOrdenadas[0]
                setCotasGuardadas(ultimaCota)
                setCotaSeleccionadaId(ultimaCota.id) // Establecer la última como seleccionada
                
                // Si el modal está abierto, cargar la nueva medida seleccionada
                if (modalCotasVisible) {
                  await cargarMedidaSeleccionada(ultimaCota)
                }
              } else {
                setCotasGuardadas(null)
                setTodasLasCotasGuardadas([])
                setCotaSeleccionadaId(null)
                // Cerrar el modal si no hay más medidas
                setModalCotasVisible(false)
                setImagenCotasUrl(null)
                coordenadasCotasRef.current = []
                setCoordenadasCotas([])
                setMedidasCotas([])
              }
            }
          } else {
            toast.error('Error al eliminar las medidas')
          }
        } catch (error) {
          console.error('Error al eliminar medidas:', error)
          toast.error('Error al eliminar las medidas')
        }
      },
    })
  }

  // Función para guardar todo en el endpoint
  const handleGuardarTodo = async () => {
    try {
      const todasLasCoordenadas = [...coordenadas, ...medidasManuales]
      
      if (todasLasCoordenadas.length === 0) {
        toast.error('No hay medidas para guardar')
        return
      }

      // Determinar qué gs_url usar: primero gsurlRecortado, sino gsurlOriginal
      const gsUrlFinal = gsurlRecortado || gsurlOriginal
      
      if (!gsUrlFinal) {
        toast.error('No hay imagen disponible para guardar')
        return
      }

      // Obtener el revision_id del material
      const revisionId = material?.revision_id
      if (!revisionId) {
        toast.error('No se encontró el ID de revisión')
        return
      }

      // Formatear las coordenadas según el formato requerido
      // Guardar toda la información de las coordenadas normalizadas (x_ini, y_ini, x_fin, y_fin para líneas, o x, y, radio para círculos)
      const jsonCoordenadas = todasLasCoordenadas
        .filter(cota => cota != null && cota.coordenadas_normalizadas != null) // Filtrar cotas inválidas
        .map((cota, index) => {
        const coords = cota.coordenadas_normalizadas
        let coordenadaCompleta = {}
        let geometriaCotaValue = null
        let lineasARepuestoArray = []

        // Si la cota tiene geometria_cota como objeto, procesarlo
        if (cota && cota.geometria_cota && !Array.isArray(cota.geometria_cota)) {
          const geometria = cota.geometria_cota
          if (geometria && geometria.x_ini !== undefined && geometria.y_ini !== undefined && 
              geometria.x_fin !== undefined && geometria.y_fin !== undefined) {
            geometriaCotaValue = {
              x_ini: Math.round(geometria.x_ini),
              y_ini: Math.round(geometria.y_ini),
              x_fin: Math.round(geometria.x_fin),
              y_fin: Math.round(geometria.y_fin)
            }
          }
        }
        // Si la cota tiene geometria_cota como array, procesarlo
        else if (cota && cota.geometria_cota && Array.isArray(cota.geometria_cota)) {
          geometriaCotaValue = cota.geometria_cota
            .filter(geometria => geometria != null && typeof geometria === 'object')
            .map(geometria => {
              if (geometria && geometria.x_ini !== undefined && geometria.y_ini !== undefined && 
                  geometria.x_fin !== undefined && geometria.y_fin !== undefined) {
                return {
                  x_ini: Math.round(geometria.x_ini),
                  y_ini: Math.round(geometria.y_ini),
                  x_fin: Math.round(geometria.x_fin),
                  y_fin: Math.round(geometria.y_fin)
                }
              }
              return geometria
            })
        }

        // Si la cota tiene lineas_a_repuesto como array, procesarlo
        if (cota && cota.lineas_a_repuesto && Array.isArray(cota.lineas_a_repuesto)) {
          lineasARepuestoArray = cota.lineas_a_repuesto
            .filter(linea => linea != null && typeof linea === 'object') // Filtrar elementos null/undefined
            .map(linea => {
              if (linea && linea.x_ini !== undefined && linea.y_ini !== undefined && 
                  linea.x_fin !== undefined && linea.y_fin !== undefined) {
                return {
                  x_ini: Math.round(linea.x_ini),
                  y_ini: Math.round(linea.y_ini),
                  x_fin: Math.round(linea.x_fin),
                  y_fin: Math.round(linea.y_fin)
                }
              }
              return linea
            })
        }

        // Si es una línea, guardar x_ini, y_ini, x_fin, y_fin
        if (coords.x_ini !== undefined && coords.y_ini !== undefined && 
            coords.x_fin !== undefined && coords.y_fin !== undefined) {
          coordenadaCompleta = {
            x_ini: Math.round(coords.x_ini),
            y_ini: Math.round(coords.y_ini),
            x_fin: Math.round(coords.x_fin),
            y_fin: Math.round(coords.y_fin)
          }
        }
        // Si es un círculo, guardar x, y, radio
        else if (coords.x !== undefined && coords.y !== undefined && coords.radio !== undefined) {
          coordenadaCompleta = {
            x: Math.round(coords.x),
            y: Math.round(coords.y),
            radio: Math.round(coords.radio)
          }
        }
        // Si solo tiene x, y (punto), guardar x, y
        else if (coords.x !== undefined && coords.y !== undefined) {
          coordenadaCompleta = {
            x: Math.round(coords.x),
            y: Math.round(coords.y)
          }
        }
        const nombreFinal = descripcionesEditadas[index] !== undefined && descripcionesEditadas[index] !== ''
          ? descripcionesEditadas[index]
          : (cota.descripcion_tecnica || cota.label || '')

        // Convertir la unidad de medida a la letra correspondiente (M = mm, C = cm, etc.)
        let unidadLetra = 'M' // Por defecto mm
        if (unidadMedida === 'cm') {
          unidadLetra = 'C'
        } else if (unidadMedida === 'm') {
          unidadLetra = 'M'
        } else if (unidadMedida === 'mm') {
          unidadLetra = 'M'
        }

        // Calcular coordenadas_etiqueta si no existe
        let coordenadasEtiqueta = cota.coordenadas_etiqueta
        if (!coordenadasEtiqueta) {
          coordenadasEtiqueta = calcularCoordenadasEtiqueta(cota)
        }

        const resultado = {
          medida: unidadLetra, // La unidad de medida (M = mm, C = cm, etc.)
          nombre: nombreFinal,
          titulo: cota.label,
          coordenada: coordenadaCompleta
        }

        // Agregar geometria_cota si existe (puede ser objeto o array)
        // Siempre guardar geometria_cota si existe, incluso si es null, para preservar la estructura
        if (cota.geometria_cota !== undefined && cota.geometria_cota !== null) {
          resultado.geometria_cota = geometriaCotaValue !== null ? geometriaCotaValue : cota.geometria_cota
        }

        // Agregar lineas_a_repuesto siempre, incluso si está vacío, para preservar la estructura
        resultado.lineas_a_repuesto = lineasARepuestoArray

        // Agregar coordenadas_etiqueta si se calculó
        if (coordenadasEtiqueta) {
          resultado.coordenadas_etiqueta = coordenadasEtiqueta
        }

        return resultado
      })

      // Construir json_medidas con las medidas tomadas y validar que todas tengan valores
      const jsonMedidas = []
      const medidasFaltantes = []

      // Convertir la unidad de medida a la letra correspondiente (M = mm, C = cm, etc.)
      let unidadLetra = 'M' // Por defecto mm
      if (unidadMedida === 'cm') {
        unidadLetra = 'C'
      } else if (unidadMedida === 'm') {
        unidadLetra = 'M'
      } else if (unidadMedida === 'mm') {
        unidadLetra = 'M'
      }

      todasLasCoordenadas
        .filter(cota => cota != null) // Filtrar cotas inválidas
        .forEach((cota, index) => {
        // Extraer solo la letra del label (A, B, C, etc.) para mostrar en el error
        let medidaLabel = cota.label?.match(/^([A-Z])/)?.[1]
        if (!medidaLabel && cota.referencia_manual?.etiqueta_estandar) {
          medidaLabel = cota.referencia_manual.etiqueta_estandar.match(/^([A-Z])/)?.[1]
        }
        if (!medidaLabel) {
          medidaLabel = String.fromCharCode(65 + index)
        }

        const valorMedida = medidasEditadas[index]
        
        // Validar que la medida tenga un valor válido
        if (!valorMedida || valorMedida === '' || valorMedida === null || valorMedida === undefined) {
          medidasFaltantes.push({
            medida: medidaLabel,
            nombre: descripcionesEditadas[index] || cota.descripcion_tecnica || cota.label || ''
          })
          return // No agregar esta medida al array
        }

        const valorNumerico = parseFloat(valorMedida)
        
        // Validar que el valor sea un número válido y no sea NaN
        if (Number.isNaN(valorNumerico) || !Number.isFinite(valorNumerico)) {
          medidasFaltantes.push({
            medida: medidaLabel,
            nombre: descripcionesEditadas[index] || cota.descripcion_tecnica || cota.label || ''
          })
          return // No agregar esta medida al array
        }

        // Solo agregar medidas con valores válidos
        // La unidad de medida se guarda en el campo "medida" (M = mm, C = cm, etc.)
        jsonMedidas.push({
          medida: unidadLetra, // La unidad de medida (M = mm, C = cm, etc.)
          valor: valorNumerico
        })
      })

      // Si falta alguna medida obligatoria, no permitir guardar
      if (medidasFaltantes.length > 0) {
        const medidasFaltantesLabels = medidasFaltantes.map(m => `${m.medida} (${m.nombre})`).join(', ')
        toast.error(`Faltan medidas obligatorias: ${medidasFaltantesLabels}. Por favor complete todas las medidas antes de guardar.`)
        return
      }

      // Validar que haya al menos una medida válida
      if (jsonMedidas.length === 0) {
        toast.error('No hay medidas válidas para guardar. Por favor complete al menos una medida.')
        return
      }

      // Validar que todas las coordenadas tengan su medida correspondiente
      if (jsonMedidas.length !== todasLasCoordenadas.length) {
        toast.error('Todas las medidas son obligatorias. Por favor complete todas las medidas antes de guardar.')
        return
      }

      // Verificar que las referencias se estén guardando correctamente
      console.log('💾 Guardando coordenadas con referencias:', jsonCoordenadas.map(c => ({
        titulo: c.titulo,
        tieneGeometria: !!c.geometria_cota,
        tieneLineas: !!c.lineas_a_repuesto,
        lineasCount: c.lineas_a_repuesto?.length || 0
      })))
      
      // Llamar al endpoint
      const resultado = await postCotas(revisionId, gsUrlFinal, jsonCoordenadas, jsonMedidas)
      
      if (resultado) {
        toast.success('Medidas guardadas exitosamente')
        
        // Recargar las cotas guardadas para obtener la información actualizada
        try {
          const response = await getImagenCotas(revisionId)
          if (response && response.success && response.success.length > 0) {
            // Guardar todas las cotas ordenadas por id descendente
            const cotasOrdenadas = response.success.sort((a, b) => b.id - a.id)
            setTodasLasCotasGuardadas(cotasOrdenadas)
            // Obtener el último item (mayor id) para mantener compatibilidad
            const ultimaCota = cotasOrdenadas[0]
            setCotasGuardadas(ultimaCota)
            setCotaSeleccionadaId(ultimaCota.id) // Establecer la última como seleccionada
          } else {
            setCotasGuardadas(null)
            setTodasLasCotasGuardadas([])
            setCotaSeleccionadaId(null)
          }
        } catch (error) {
          console.error('Error al recargar cotas guardadas:', error)
        }
      } else {
        toast.error('Error al guardar las medidas')
      }
    } catch (error) {
      console.error('❌ Error al guardar medidas:', error)
      toast.error('Error al guardar las medidas')
    }
  }

  // Función para actualizar el nombre en el estado local
  // ==================== FUNCIONES: Edición de Medidas ====================
  const handleCambiarNombreMedida = (index, nuevoNombre) => {
    const esManual = index >= coordenadas.length
    
    // Para medidas manuales, solo permitir una letra (A-Z)
    if (esManual) {
      // Filtrar solo letras y tomar la primera
      const letras = nuevoNombre.replace(/[^A-Za-z]/g, '').toUpperCase()
      const letraUnica = letras.charAt(0) || ''
      
      setNombresEditados(prev => ({
        ...prev,
        [index]: letraUnica
      }))
    } else {
      // Para medidas no manuales, permitir cualquier texto
      setNombresEditados(prev => ({
        ...prev,
        [index]: nuevoNombre
      }))
    }
  }

  // Función para actualizar la descripción en el estado local
  const handleCambiarDescripcionMedida = (index, nuevaDescripcion) => {
    setDescripcionesEditadas(prev => ({
      ...prev,
      [index]: nuevaDescripcion
    }))
  }

  // Función para guardar el nombre cuando se pierde el foco
  const handleBlurNombreMedida = (index) => {
    const nuevoNombre = nombresEditados[index] !== undefined ? nombresEditados[index] : ''
    const esManual = index >= coordenadas.length
    
    if (esManual) {
      // Validar que sea solo una letra (A-Z) para medidas manuales
      if (nuevoNombre) {
        // Extraer solo la primera letra y convertir a mayúscula
        const letraUnica = nuevoNombre.trim().charAt(0).toUpperCase()
        if (!/^[A-Z]$/.test(letraUnica)) {
          toast.error('El nombre debe ser una sola letra (A-Z)')
          // Restaurar el valor anterior
          const todasLasCoordenadas = [...coordenadas, ...medidasManuales]
          const cota = todasLasCoordenadas[index]
          if (cota) {
            setNombresEditados(prev => ({
              ...prev,
              [index]: cota.label || ''
            }))
          }
          return
        }
        
        // Verificar que la letra no esté en uso por otra medida
        const todasLasCoordenadas = [...coordenadas, ...medidasManuales]
        const letraEnUso = todasLasCoordenadas.some((cota, idx) => {
          if (idx === index) return false
          const letraCota = extraerLetraLabel(cota)
          return letraCota === letraUnica
        })
        
        if (letraEnUso) {
          toast.error(`La letra "${letraUnica}" ya está en uso por otra medida`)
          // Restaurar el valor anterior
          const cota = todasLasCoordenadas[index]
          if (cota) {
            setNombresEditados(prev => ({
              ...prev,
              [index]: cota.label || ''
            }))
          }
          return
        }
        
        // Actualizar con la letra única
        const indexManual = index - coordenadas.length
        setMedidasManuales(prev => {
          const nuevas = [...prev]
          if (!nuevas[indexManual]) return nuevas
          
          nuevas[indexManual] = {
            ...nuevas[indexManual],
            label: letraUnica,
            referencia_manual: {
              ...nuevas[indexManual].referencia_manual,
              etiqueta_estandar: letraUnica
            }
          }
          return nuevas
        })
        
        // Actualizar también el estado de nombres editados
        setNombresEditados(prev => ({
          ...prev,
          [index]: letraUnica
        }))
      }
    } else {
      setCoordenadas(prev => {
        const nuevas = [...prev]
        if (!nuevas[index]) return nuevas
        
        nuevas[index] = {
          ...nuevas[index],
          label: nuevoNombre
        }
        return nuevas
      })
    }
  }

  // Función para guardar la descripción cuando se pierde el foco
  const handleBlurDescripcionMedida = (index) => {
    const nuevaDescripcion = descripcionesEditadas[index] !== undefined ? descripcionesEditadas[index] : ''
    const esManual = index >= coordenadas.length
    
    if (esManual) {
      const indexManual = index - coordenadas.length
      setMedidasManuales(prev => {
        const nuevas = [...prev]
        if (nuevas[indexManual]) {
          nuevas[indexManual] = {
            ...nuevas[indexManual],
            descripcion_tecnica: nuevaDescripcion,
            referencia_manual: {
              ...nuevas[indexManual].referencia_manual,
              definicion: nuevaDescripcion
            }
          }
        }
        return nuevas
      })
    } else {
      setCoordenadas(prev => {
        const nuevas = [...prev]
        if (nuevas[index]) {
          nuevas[index] = {
            ...nuevas[index],
            descripcion_tecnica: nuevaDescripcion
          }
        }
        return nuevas
      })
    }
  }

  // Función para borrar una medida
  const handleBorrarMedida = (index) => {
    const esManual = index >= coordenadas.length
    
    // Función auxiliar para reindexar un objeto de estado
    const reindexarEstado = (prev) => {
      const nuevas = {}
      Object.keys(prev).forEach(oldIndex => {
        const oldIdx = parseInt(oldIndex, 10)
        if (oldIdx < index) {
          // Los índices anteriores se mantienen igual
          nuevas[oldIdx] = prev[oldIdx]
        } else if (oldIdx > index) {
          // Los índices posteriores se desplazan uno hacia atrás
          nuevas[oldIdx - 1] = prev[oldIdx]
        }
        // El índice que se borra se omite
      })
      return nuevas
    }
    
    // Reindexar todos los estados locales antes de borrar
    setMedidasEditadas(prev => reindexarEstado(prev))
    setNombresEditados(prev => reindexarEstado(prev))
    setDescripcionesEditadas(prev => reindexarEstado(prev))
    
    if (esManual) {
      const indexManual = index - coordenadas.length
      // Borrar de medidasManuales
      setMedidasManuales(prev => {
        const nuevas = prev.filter((_, i) => i !== indexManual)
        return nuevas
      })
    } else {
      // Borrar de coordenadas
      setCoordenadas(prev => {
        const nuevas = prev.filter((_, i) => i !== index)
        return nuevas
      })
    }
  }

  // Función para borrar una línea de referencia específica de una medida
  // Handler para seleccionar una línea de referencia desde el panel
  const handleSeleccionarLineaReferencia = (medidaIndex, lineaIndex) => {
    console.log('🔵 handleSeleccionarLineaReferencia llamado:', { medidaIndex, lineaIndex })
    console.log('🔵 Estado actual lineaReferenciaSeleccionada:', lineaReferenciaSeleccionada)
    
    // Si ya está seleccionada, deseleccionarla
    if (lineaReferenciaSeleccionada && 
        lineaReferenciaSeleccionada.medidaIndex === medidaIndex &&
        lineaReferenciaSeleccionada.lineaIndex === lineaIndex) {
      console.log('🔵 Deseleccionando línea')
      setLineaReferenciaSeleccionada(null)
      setArrastrandoPunto(null)
    } else {
      // Seleccionar la línea de referencia
      console.log('🔵 Estableciendo nueva línea seleccionada:', { medidaIndex, lineaIndex })
      setLineaReferenciaSeleccionada({ medidaIndex, lineaIndex })
      setArrastrandoPunto(null)
      // Deseleccionar cualquier medida seleccionada
      setMedidaSeleccionada(null)
      // Cancelar cualquier modo de dibujo activo
      setModoDibujo(null)
      setMedidaIndexParaLineaReferencia(null)
      
      console.log('🔵 Estado actualizado, debería re-renderizar')
    }
  }

  const handleBorrarLineaReferencia = (medidaIndex, lineaIndex) => {
    const esManual = medidaIndex >= coordenadas.length
    
    if (esManual) {
      const indexManual = medidaIndex - coordenadas.length
      setMedidasManuales(prev => {
        const nuevas = [...prev]
        if (nuevas[indexManual] && nuevas[indexManual].lineas_a_repuesto) {
          nuevas[indexManual] = {
            ...nuevas[indexManual],
            lineas_a_repuesto: nuevas[indexManual].lineas_a_repuesto.filter((_, i) => i !== lineaIndex)
          }
        }
        return nuevas
      })
    } else {
      setCoordenadas(prev => {
        const nuevas = [...prev]
        if (nuevas[medidaIndex] && nuevas[medidaIndex].lineas_a_repuesto) {
          nuevas[medidaIndex] = {
            ...nuevas[medidaIndex],
            lineas_a_repuesto: nuevas[medidaIndex].lineas_a_repuesto.filter((_, i) => i !== lineaIndex)
          }
        }
        return nuevas
      })
    }
  }

  // Función para activar el modo de agregar línea de referencia a una medida específica
  const handleAgregarLineaReferencia = (medidaIndex) => {
    // Si ya está activo para esta medida, cancelar
    if (medidaIndexParaLineaReferencia === medidaIndex && puntoALineaReferencia) {
      setMedidaIndexParaLineaReferencia(null)
      setPuntoALineaReferencia(null)
      setPuntoBLineaReferencia(null)
      setPuntoBLineaReferenciaConfirmado(false)
      return
    }
    
    // Activar modo para esta medida
    setMedidaIndexParaLineaReferencia(medidaIndex)
    setPuntoALineaReferencia(null)
    setPuntoBLineaReferencia(null)
    setPuntoBLineaReferenciaConfirmado(false)
    
    // Cancelar otros modos de dibujo
    setModoDibujo(null)
    setPuntoA(null)
    setPuntoBTemporal(null)
    setPuntoBConfirmado(false)
    setCentroCirculo(null)
    setPuntoRadioTemporal(null)
    setRadioConfirmado(false)
  }

  // Función para confirmar y agregar la línea de referencia
  const handleConfirmarLineaReferencia = () => {
    if (medidaIndexParaLineaReferencia === null || !puntoALineaReferencia || !puntoBLineaReferencia) {
      return
    }

    const nuevaLinea = {
      x_ini: puntoALineaReferencia.x,
      y_ini: puntoALineaReferencia.y,
      x_fin: puntoBLineaReferencia.x,
      y_fin: puntoBLineaReferencia.y
    }

    const esManual = medidaIndexParaLineaReferencia >= coordenadas.length

    if (esManual) {
      const indexManual = medidaIndexParaLineaReferencia - coordenadas.length
      setMedidasManuales(prev => {
        const nuevas = [...prev]
        if (!nuevas[indexManual]) return nuevas
        
        nuevas[indexManual] = {
          ...nuevas[indexManual],
          lineas_a_repuesto: [
            ...(nuevas[indexManual].lineas_a_repuesto || []),
            nuevaLinea
          ]
        }
        return nuevas
      })
    } else {
      setCoordenadas(prev => {
        const nuevas = [...prev]
        if (!nuevas[medidaIndexParaLineaReferencia]) return nuevas
        
        nuevas[medidaIndexParaLineaReferencia] = {
          ...nuevas[medidaIndexParaLineaReferencia],
          lineas_a_repuesto: [
            ...(nuevas[medidaIndexParaLineaReferencia].lineas_a_repuesto || []),
            nuevaLinea
          ]
        }
        return nuevas
      })
    }

    // Resetear el modo
    setMedidaIndexParaLineaReferencia(null)
    setPuntoALineaReferencia(null)
    setPuntoBLineaReferencia(null)
    setPuntoBLineaReferenciaConfirmado(false)
  }

  const handleCrearEmbalaje = async (nombreEmbalaje) => {
    try {
      const response = await postCrearEmbalaje({ nombreEmbalaje })
      if (response && response.success && response.success > 0) {
        toast.success('Embalaje creado exitosamente')
        
        // Agregar temporalmente el nuevo embalaje a la lista local
        const nuevoEmbalajeObj = {
          id: response.success,
          nombre: nombreEmbalaje
        }
        setTiposEmbalajeLocal(prev => [...prev, nuevoEmbalajeObj])
        
        // Actualizar la lista de embalajes llamando a onResetData
        onResetData?.()
        onResetAllData?.()
        return response.success
      }
      toast.error('Error al crear el embalaje')
      return false
    } catch (error) {
      toast.error('Error al crear el embalaje')
      return false
    }
  }

  const handleAgregarEmbalaje = () => {
    if (!nuevoEmbalaje.trim()) {
      toast.error('Por favor ingrese un nombre para el embalaje')
      return
    }

    confirm({
      title: `Crear el nuevo embalaje "${nuevoEmbalaje}"?`,
      content: '¿Está seguro de que desea crear este nuevo embalaje?',
      onOk: async () => {
        const nuevoId = await handleCrearEmbalaje(nuevoEmbalaje)
        if (nuevoId) {
          // Establecer el nuevo embalaje como seleccionado
          form.setFieldsValue({ embalaje: nuevoId })
          setNuevoEmbalaje('')
          
          // Forzar re-render del Select para mostrar el nuevo valor
          setTimeout(() => {
            form.validateFields(['embalaje'])
          }, 100)
        }
      },
      onCancel: () => {
        setNuevoEmbalaje('')
      },
    })
  }

  const handleCrearEmpaque = async (nombreEmpaque) => {
    try {
      const response = await postCrearEmpaque({ nombreEmpaque })
      if (response && response.success && response.success > 0) {
        toast.success('Empaque creado exitosamente')
        
        // Agregar temporalmente el nuevo embalaje a la lista local
        const nuevoEmpaqueObj = {
          id: response.success,
          nombre: nombreEmpaque
        }
        setTiposEmpaqueLocal(prev => [...prev, nuevoEmpaqueObj])
        
        // Actualizar la lista de embalajes llamando a onResetData
        onResetData?.()
        onResetAllData?.()
        return response.success
      }
      toast.error('Error al crear el embalaje')
      return false
    } catch (error) {
      toast.error('Error al crear el embalaje')
      return false
    }
  }

  const handleCrearProteccion = async (nombreProteccion) => {
    try {
      const response = await postCrearProteccion({ nombreProteccion })
      if (response && response.success && response.success > 0) {
        toast.success('Protección creada exitosamente')
        
        // Agregar temporalmente el nuevo embalaje a la lista local
        const nuevoProteccionObj = {
          id: response.success,
          nombre: nombreProteccion
        }
        setTiposProteccionLocal(prev => [...prev, nuevoProteccionObj])
        
        // Actualizar la lista de embalajes llamando a onResetData
        onResetData?.()
        onResetAllData?.()
        return response.success
      }
      toast.error('Error al crear la protección')
      return false
    } catch (error) {
      toast.error('Error al crear la protección')
      return false
    }
  }
  

  const handleAgregarEmpaque = () => {
    if (!nuevoEmpaque.trim()) {
      toast.error('Por favor ingrese un nombre para el empaque')
      return
    }

    confirm({
      title: `Crear el nuevo empaque "${nuevoEmpaque}"?`,
      content: '¿Está seguro de que desea crear este nuevo empaque?',
      onOk: async () => {
        const nuevoId = await handleCrearEmpaque(nuevoEmpaque)
        if (nuevoId) {
          // Establecer el nuevo embalaje como seleccionado
          form.setFieldsValue({ tipoEmpaque: nuevoId })
          setNuevoEmpaque('')
          
          // Forzar re-render del Select para mostrar el nuevo valor
          setTimeout(() => {
            form.validateFields(['tipoEmpaque'])
          }, 100)
        }
      },
      onCancel: () => {
        setNuevoEmpaque('')
      },
    })
  }

  const handleAgregarProteccion = () => {
    if (!nuevoProteccion.trim()) {
      toast.error('Por favor ingrese un nombre para la protección')
      return
    }

    confirm({
      title: `Crear la nueva protección "${nuevoProteccion}"?`,
      content: '¿Está seguro de que desea crear este nuevo protección?',
      onOk: async () => {
        const nuevoId = await handleCrearProteccion(nuevoProteccion)
        if (nuevoId) {
          // Agregar la nueva protección a las ya seleccionadas
          const proteccionesActuales = form.getFieldValue('proteccion') || []
          form.setFieldsValue({ proteccion: [...proteccionesActuales, nuevoId] })
          setNuevoProteccion('')
          
          // Forzar re-render del Select para mostrar el nuevo valor
          setTimeout(() => {
            form.validateFields(['proteccion'])
          }, 100)
        }
      },
      onCancel: () => {
        setNuevoProteccion('')
      },
    })
  }

  const handleCrearMarcaFabrica = async (nombreMarcaFabrica) => {
    try {
      const response = await postCrearMarcaFabrica({ nombreMarcaFabrica })
      if (response && response.success && response.success > 0) {
        toast.success('Marca fábrica creada exitosamente')

        const nuevoMarcaFabricaObj = {
          id: response.success,
          nombre: nombreMarcaFabrica
        }
        setMarcasFabricaLocal(prev => [...prev, nuevoMarcaFabricaObj])

        onResetData?.()
        onResetAllData?.()
        return response.success
      }
      toast.error('Error al crear la marca fábrica')
      return false
    } catch (error) {
      toast.error('Error al crear la marca fábrica')
      return false
    }
  }

  const handleAgregarMarcaFabrica = () => {
    if (!nuevoMarcaFabrica.trim()) {
      toast.error('Por favor ingrese un nombre para la marca fábrica')
      return
    }

    confirm({
      title: `Crear la nueva marca fábrica "${nuevoMarcaFabrica}"?`,
      content: '¿Está seguro de que desea crear esta nueva marca fábrica?',
      onOk: async () => {
        const nuevoId = await handleCrearMarcaFabrica(nuevoMarcaFabrica)
        if (nuevoId) {
          form.setFieldsValue({ marcaFabrica: nuevoId })
          setNuevoMarcaFabrica('')

          setTimeout(() => {
            form.validateFields(['marcaFabrica'])
          }, 100)
        }
      },
      onCancel: () => {
        setNuevoMarcaFabrica('')
      },
    })
  }

  // Función para llenar campos obligatorios con valores por defecto
  const llenarCamposObligatorios = async () => {
    // Obtener primeros valores disponibles de cada lista
    let primeraCategoria = categorias && categorias.length > 0 ? categorias[0].id : null
    let primeraSubcategoria = subCategorias && subCategorias.length > 0 ? subCategorias[0].id : null
    let primerSegmento = segmentos && segmentos.length > 0 ? segmentos[0].id : null
    let primerSubsegmento = subSegmentos && subSegmentos.length > 0 ? subSegmentos[0].id : null

    // Con cascada activa se debe recorrer nivel por nivel respetando los padres
    if (cascada.activa) {
      primeraCategoria = cascada.categorias[0]?.id ?? null
      const subcategoriasCascada = primeraCategoria != null
        ? await cascada.cambiarCategoria(primeraCategoria)
        : []
      primeraSubcategoria = subcategoriasCascada[0]?.id ?? null
      const segmentosCascada = primeraSubcategoria != null
        ? await cascada.cambiarSubcategoria(primeraSubcategoria)
        : []
      primerSegmento = segmentosCascada[0]?.id ?? null
      const subsegmentosCascada = primerSegmento != null
        ? await cascada.cambiarSegmento(primerSegmento)
        : []
      primerSubsegmento = subsegmentosCascada[0]?.id ?? null
    }
    const primeraMarca = marcas && marcas.length > 0 ? marcas[0].id : null
    const primerTipoEmpaque = tiposEmpaqueLocal && tiposEmpaqueLocal.length > 0 ? tiposEmpaqueLocal[0].id : null
    const primeraProteccion = tiposProteccionLocal && tiposProteccionLocal.length > 0 ? tiposProteccionLocal[0].id : null
    const primerEmbalaje = tiposEmbalajeLocal && tiposEmbalajeLocal.length > 0 ? tiposEmbalajeLocal[0].id : null
    const primerModelo = modelosAplicacionOrigen && modelosAplicacionOrigen.length > 0 ? modelosAplicacionOrigen[0].id : null

    // Llenar campos del formulario
    form.setFieldsValue({
      descripcionBreve: 'DESCRIPCION BREVE TEST',
      descripcionCorrecta: 'DESCRIPCION CORRECTA TEST',
      observacion: 'OBSERVACION TEST',
      cambioEtiqueta: 'No',
      cambioEmpaque: 'No',
      categoria: primeraCategoria,
      subcategoria: primeraSubcategoria,
      segmento: primerSegmento,
      subsegmento: primerSubsegmento,
      marca: primeraMarca,
      estadoDefinido: 'ACTIVO',
      usoAplicacion: 'USO APLICACION TEST',
      largo: '100',
      ancho: '50',
      alto: '30',
      diametro: '25',
      peso: '500',
      composicion: 'COMPOSICION TEST',
      especificacionesElectricas: 'NA',
      especificacionesProducto: 'ESPECIFICACIONES TEST',
      tipoEmpaque: primerTipoEmpaque,
      proteccion: primeraProteccion ? [primeraProteccion] : [],
      embalaje: primerEmbalaje,
      unidadEmpaqueRecomendada: '1',
    })

    // Agregar modelos de aplicación si hay disponibles
    if (primerModelo && modelosAplicacion.length === 0) {
      setModelosAplicacion([{ id: primerModelo, nombre: modelosAplicacionOrigen[0].nombre || 'MODELO TEST' }])
    }
    if (primerModelo && modelosAplicacionPrincipal.length === 0) {
      setModelosAplicacionPrincipal([{ id: primerModelo, nombre: modelosAplicacionOrigen[0].nombre || 'MODELO TEST' }])
    }

    toast.success('Campos obligatorios llenados automáticamente')
  }

  // Manejar triple click
  const handleTripleClick = () => {
    if (isReadOnly) return
    // Limpiar timeout anterior
    if (clickTimeoutRef.current) {
      clearTimeout(clickTimeoutRef.current)
    }
    
    const newCount = clickCount + 1
    setClickCount(newCount)
    
    // Si son 3 clicks, llenar campos
    if (newCount === 3) {
      llenarCamposObligatorios()
      setClickCount(0)
    } else {
      // Resetear contador después de 500ms
      clickTimeoutRef.current = setTimeout(() => {
        setClickCount(0)
      }, 500)
    }
  }

  return (
    <div className="axioma-form-container">
      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
      <div
        className="axioma-form-container-card"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
      >
        {/* Botón oculto para llenar campos obligatorios */}
        <button
          type="button"
          style={{
            position: 'absolute',
            top: '10px',
            left: '10px',
            width: '20px',
            height: '20px',
            backgroundColor: 'transparent',
            border: 'none',
            cursor: 'pointer',
            zIndex: 1000,
            padding: 0,
            margin: 0,
          }}
          onClick={(e) => {
            e.stopPropagation()
            handleTripleClick()
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.stopPropagation()
              handleTripleClick()
            }
          }}
          title="Triple click para llenar campos obligatorios"
          aria-label="Triple click para llenar campos obligatorios"
        />
        {categorytoCompare && (
          <CompareMaterialsComponent
            isReadOnly={isReadOnly}
            onClose={() => {
              setCategorytoCompare(null)
            }}
            category={categorytoCompare}
            repuestos={repuestosData}
            material={material}
            homologos={homologos}
            reemplazantes={reemplazantes}
            modelosAplicacionPrincipal={modelosAplicacionPrincipal}
            modelosAplicacion={modelosAplicacion}
            onAddHomologo={(nuevoHomologo) => {
              setHomologos(prev => [...prev, nuevoHomologo])
            }}
            onAddReemplazante={(nuevoReemplazante) => {
              setReemplazantes(prev => [...prev, nuevoReemplazante])
            }}
            onAddModeloAplicacion={(nuevoModelo) => {
              setModelosAplicacion(prev => {
                const exists = prev.some(m => m.repuesto_id === nuevoModelo.repuesto_id)
                return exists ? prev : [...prev, nuevoModelo]
              })
            }}
            onAddModeloAplicacionPrincipal={(nuevoModelo) => {
              setModelosAplicacionPrincipal(prev => [...prev, nuevoModelo])
            }}
            onRemoveHomologo={(repuestoId) => {
              setHomologos(prev => prev.filter(h => h.repuesto_id !== repuestoId))
            }}
            onRemoveReemplazante={(repuestoId) => {
              setReemplazantes(prev => prev.filter(r => r.repuesto_id !== repuestoId))
            }}
            onRemoveModeloAplicacion={(repuestoId) => {
              setModelosAplicacion(prev => prev.filter(m => m.repuesto_id !== repuestoId))
            }}
            onRemoveModeloAplicacionPrincipal={(repuestoId) => {
              setModelosAplicacionPrincipal(prev => prev.filter(m => m.repuesto_id !== repuestoId))
            }}
          />
        )}

        {categorytoCompareModelMoto && (
          <CompareMotoModelsComponent
            isReadOnly={isReadOnly}
            onClose={() => {
              setCategorytoCompareModelMoto(null)
            }}
            onResetData={onResetData}
            category={categorytoCompareModelMoto}
            repuestos={repuestosData}
            material={material}
            marcas={marcas}
            homologos={homologos}
            reemplazantes={reemplazantes}
            modelosAplicacionPrincipal={modelosAplicacionPrincipal}
            modelosAplicacion={modelosAplicacion}
            modelosAplicacionOrigen={modelosAplicacionOrigen}
            onResetAllData={onResetAllData}
            onAddModeloAplicacion={(nuevoModelo) => {
              setModelosAplicacion(prev => {
                const exists = prev.some(m => m.id === nuevoModelo.id)
                return exists ? prev : [...prev, nuevoModelo]
              })
            }}
            onAddModeloAplicacionPrincipal={(nuevoModelo) => {
              setModelosAplicacionPrincipal(prev => [...prev, nuevoModelo])
            }}
            onRemoveModeloAplicacion={(modeloId) => {
              setModelosAplicacion(prev => prev.filter(m => m.id !== modeloId))
            }}
            onRemoveModeloAplicacionPrincipal={(modeloId) => {
              setModelosAplicacionPrincipal(prev => prev.filter(m => m.id !== modeloId))
            }}
          />
        )}

        <Form
          form={form}
          layout="vertical"
          disabled={isReadOnly}
          onFinish={() => handleSubmitSave(form.getFieldsValue(), true)}
          initialValues={{
            categoria: material.revision_categoria_sugerida_id,
            descripcionBreve: material.revision_descripcion_breve,
            descripcionCorrecta: material.revision_descripcion_correcta,
            cambioDescripcion: '',
            comentarioAuditor: material.revision_comentario_auditor,
            observacion: material.revision_observacion,
            subcategoria: material.revision_subcategoria_sugerida_id,
            segmento: material.revision_segmento_sugerido_id,
            subsegmento: material.revision_subsegmento_sugerido_id,
            marca: material.revision_marca_sugerida_id,
            estadoDefinido: material.revision_estado_ax,
            usoAplicacion: material.revision_uso_aplicacion,
            largo: material.revision_largo_mm,
            ancho: parseFloat(material.revision_ancho_mm),
            alto: parseFloat(material.revision_alto_mm),
            diametro: parseFloat(material.revision_diametro_mm),
            peso: parseFloat(material.revision_peso_g),
            composicion: material.revision_composicion,
            especificacionesElectricas:
              material.revision_especificaciones_electricas,
            especificacionesProducto:
              material.revision_especificaciones_producto,
            tipoEmpaque: material.revision_tipo_empaque_sugerido_id,
            proteccion: material.revision_tipos_proteccion 
              ? (typeof material.revision_tipos_proteccion === 'string' 
                  ? JSON.parse(material.revision_tipos_proteccion).map(p => p.id)
                  : material.revision_tipos_proteccion.map(p => p.id))
              : [],
            embalaje: material.revision_embalaje_sugerido_id,
            unidadEmpaqueRecomendada:
              material.revision_unidad_empaque_recomendada,
            cambioEtiqueta: convertirBooleanATexto(
              material.revision_cambio_etiqueta,
            ),
            cambioEmpaque: convertirBooleanATexto(
              material.revision_cambio_empaque,
            ),
            marcaFabrica: getMarcaFabricaIdFromMaterial(material, marcasFabrica || []),
          }}
        >
          <Row gutter={[12, 0]}>
            {/* COLUMNA 1: Descripción y Categorías */}
            <Col xs={24} sm={12} md={6} className="form-column-1">
              <DescripcionSection
                form={form}
                material={material}
                isReadOnly={isReadOnly}
                loadingTextoBreve={loadingTextoBreve}
                descripcionUrgente={descripcionUrgente}
                descripcionBreveIA={descripcionBreveIA}
                descripcionBreveIAModificada={descripcionBreveIAModificada}
                camposFaltantes={camposFaltantes}
                getFieldValidation={getFieldValidation}
                onGenerarTextoBreve={handleGenerarTextoBreve}
                onDescripcionBreveChange={(e) => {
                  const nuevoValor = e.target.value
                    .toUpperCase()
                  form.setFieldsValue({ descripcionBreve: nuevoValor })
                  if (descripcionBreveIA && nuevoValor !== descripcionBreveIA) {
                    setDescripcionBreveIAModificada(1)
                  } else if (descripcionBreveIA && nuevoValor === descripcionBreveIA) {
                    setDescripcionBreveIAModificada(0)
                  }
                  if(camposFaltantes.includes('descripcionBreve')){
                    setCamposFaltantes(camposFaltantes.filter(c => c !== 'descripcionBreve'))
                  }
                }}
                onDescripcionCorrectaChange={(e) => {
                  const nuevoValor = e.target.value
                    .toUpperCase()
                    .slice(0, DESCRIPCION_CORRECTA_MAX_LENGTH)
                  form.setFieldsValue({ descripcionCorrecta: nuevoValor })
                  if(camposFaltantes.includes('descripcionCorrecta')){
                    setCamposFaltantes(camposFaltantes.filter(c => c !== 'descripcionCorrecta'))
                  }
                }}
                onDescripcionUrgenteChange={(e) => {
                  const {checked} = e.target
                  if (checked) {
                    confirm({
                      title: 'Seguro que quieres notificar este cambio de descripción como urgente?',
                      onOk: () => setDescripcionUrgente(true),
                      onCancel: () => setDescripcionUrgente(false),
                    })
                  } else {
                    setDescripcionUrgente(false)
                  }
                }}
                requiereActualizacionCatalogo={requiereActualizacionCatalogo}
                onRequiereActualizacionCatalogoChange={(value) => setRequiereActualizacionCatalogo(value)}
                onCampoChange={(campo) => {
                  if(camposFaltantes.includes(campo)){
                    setCamposFaltantes(camposFaltantes.filter(c => c !== campo))
                  }
                }}
              />
              
              <CategoriaSection
                form={form}
                material={material}
                categorias={cascada.activa ? cascada.categorias : categorias}
                subCategorias={cascada.activa ? cascada.subcategorias : subCategorias}
                segmentos={cascada.activa ? cascada.segmentos : segmentos}
                subSegmentos={cascada.activa ? cascada.subsegmentos : subSegmentos}
                cascadaActiva={cascada.activa}
                marcas={marcas}
                estadosDefinidos={estadosDefinidos}
                isReadOnly={isReadOnly}
                camposFaltantes={camposFaltantes}
                getFieldValidation={getFieldValidation}
                onCampoChange={(campo) => {
                  if(camposFaltantes.includes(campo)){
                    setCamposFaltantes(camposFaltantes.filter(c => c !== campo))
                  }
                }}
                onCategoriaChange={(value) => {
                  if (!cascada.activa) return
                  // Al cambiar el padre se limpian los hijos: la cascada siempre se respeta
                  form.setFieldsValue({ subcategoria: null, segmento: null, subsegmento: null })
                  cascada.cambiarCategoria(value)
                }}
                onSubcategoriaChange={(value) => {
                  if (!cascada.activa) return
                  form.setFieldsValue({ segmento: null, subsegmento: null })
                  cascada.cambiarSubcategoria(value)
                }}
                onSegmentoChange={(value) => {
                  if (!cascada.activa) return
                  form.setFieldsValue({ subsegmento: null })
                  cascada.cambiarSegmento(value)
                }}
              />
            </Col>

            {/* COLUMNA 2: Medidas */}
            <Col xs={24} sm={12} md={6} className="form-column-2">
              <Form.Item label="Medidas">
                  <Button
                    type="default"
                    onClick={async () => {
                      const values = form.getFieldsValue()
                      const categoriaId = values.categoria || material?.revision_categoria_sugerida_id
                      const subcategoriaId = values.subcategoria || material?.revision_subcategoria_sugerida_id
                      if (!categoriaId || !subcategoriaId) {
                        toast.error('Selecciona categoría y subcategoría antes de ver medidas')
                        return
                      }
                      const opciones = await getPlantillaMedidasOpciones(categoriaId, subcategoriaId)
                      if (!opciones || !Array.isArray(opciones) || opciones.length === 0) {
                        toast.info('No hay plantillas de medidas disponibles para esta categoría/subcategoría')
                        return
                      }
                      setOpcionesMedidas(opciones)
                      const opcionesDescripciones = opciones.map(o => o.descripcion)
                      const seleccionEnOpciones = jsonMedidasEditable?.definicion_cotas && opcionesDescripciones.includes(jsonMedidasEditable.definicion_cotas)
                      const yaTienePlantilla = jsonMedidasEditable && !Array.isArray(jsonMedidasEditable) && jsonMedidasEditable?.definicion_cotas && jsonMedidasEditable?.especificaciones?.length > 0 && seleccionEnOpciones
                      if (yaTienePlantilla) {
                        const list = (jsonMedidasEditable.especificaciones ?? []).map(e => ({
                          valor: e.tipo_campo === 'ARR' ? (e.valor ?? '') : e.valor,
                          medida: e.medida ?? '',
                          nombre: e.definicion_cotas ?? e.descripcion ?? e.nombre ?? '',
                          definicion_cotas: e.definicion_cotas ?? e.descripcion ?? e.nombre ?? '',
                          tipo_campo: e.tipo_campo ?? 'Num',
                          opciones: e.opciones ?? '',
                        }))
                        setJsonMedidasEnEdicion(
                          list.map((m, i) => ({
                            ...m,
                            _key: `medida-${i}-${Date.now()}`,
                          }))
                        )
                        setImagenVerMedidasUrl(null)
                        try {
                          const plantillaFresca = await getPlantillaMedidas(categoriaId, subcategoriaId, jsonMedidasEditable.definicion_cotas)
                          const dataFresca = plantillaFresca?.data ?? plantillaFresca
                          const procs = dataFresca?.imagenes_procesadas
                          if (Array.isArray(procs) && procs.length > 0) {
                            const urls = procs.map(p => p.signed_url).filter(Boolean)
                            const nombres = procs.map(p => (p.gs_url && typeof p.gs_url === 'string' ? p.gs_url.replace(/^.*\//, '') : '')).filter(Boolean)
                            setImagenesVerMedidasUrls(urls)
                            setImagenesVerMedidasNombres(nombres.length === urls.length ? nombres : urls.map(() => ''))
                          } else {
                            const gsUrl = dataFresca?.gs_url ?? null
                            if (gsUrl) {
                              const res = await postFirmarRevision(gsUrl)
                              setImagenesVerMedidasUrls(res?.url ? [res.url] : [])
                              setImagenesVerMedidasNombres([gsUrl.replace(/^.*\//, '')])
                            } else {
                              setImagenesVerMedidasUrls([])
                              setImagenesVerMedidasNombres([])
                            }
                          }
                        } catch {
                          setImagenesVerMedidasUrls([])
                          setImagenesVerMedidasNombres([])
                        }
                        setVerMedidasCarouselIndex(0)
                      } else {
                        setJsonMedidasEnEdicion([])
                        setImagenVerMedidasUrl(null)
                        setImagenesVerMedidasUrls([])
                        setImagenesVerMedidasNombres([])
                      }
                      setModalVerMedidasOpen(true)
                    }}
                  >
                    Ver medidas
                  </Button>
                  <Button
                    type="default"
                    icon={<ColumnWidthOutlined />}
                    style={{ marginLeft: 8 }}
                    onClick={handleAbrirImagenCotizacion}
                    disabled={
                      !imagenCotizacionGuardada &&
                      (!plantillaMedidasGuardada || !hayFotosCotizacion)
                    }
                    title={
                      imagenCotizacionGuardada
                        ? 'Ver la imagen para cotización guardada'
                        : !plantillaMedidasGuardada
                        ? 'El material no tiene medidas guardadas en la plantilla'
                        : !hayFotosCotizacion
                          ? 'Sube o toma al menos una foto del material o del empaque'
                          : 'Armar imagen para cotización con las fotos del material'
                    }
                  >
                    Imagen cotización
                  </Button>
                </Form.Item>
              <MedidasSection
                form={form}
                nombreBascula={nombreBascula}
                basculaConectada={basculaConectada}
                isReadOnly={isReadOnly}
                camposFaltantes={camposFaltantes}
                getFieldValidation={getFieldValidation}
                onCampoChange={(campo) => {
                  if(camposFaltantes.includes(campo)){
                    setCamposFaltantes(camposFaltantes.filter(c => c !== campo))
                  }
                }}
                ocultarMedidasPrincipales={tieneJsonMedidas}
                medidaActiva={activeMeasureTarget?.type === 'form' ? activeMeasureTarget.field : null}
                onMeasureFocus={(field) => setActiveMeasureTarget({ type: 'form', field })}
              />

              <Form.Item
                label="Especificaciones Eléctricas"
                name="especificacionesElectricas"
                {...getFieldValidation('especificacionesElectricas')}
              >
                <Input
                  placeholder="Ingrese especificaciones"
                  disabled={isReadOnly}
                  onChange={(e) => {
                    form.setFieldsValue({
                      especificacionesElectricas: e.target.value.toUpperCase(),
                    })
                    if(camposFaltantes.includes('especificacionesElectricas')){
                      setCamposFaltantes(camposFaltantes.filter(c => c !== 'especificacionesElectricas'))
                    }
                  }}
                />
              </Form.Item>

              <Form.Item
                label="Especificaciones del Producto"
                name="especificacionesProducto"
                {...getFieldValidation('especificacionesProducto')}
              >
                <TextArea
                  rows={1}
                  placeholder="Ingrese especificaciones del producto"
                  style={{ resize: 'none' }}
                  disabled={isReadOnly}
                  onChange={(e) => {
                    form.setFieldsValue({
                      especificacionesProducto: e.target.value.toUpperCase(),
                    })
                    if(camposFaltantes.includes('especificacionesProducto')){
                      setCamposFaltantes(camposFaltantes.filter(c => c !== 'especificacionesProducto'))
                    }
                  }}
                />
              </Form.Item>

              <Form.Item 
                label="Tipo de Empaque" 
                name="tipoEmpaque"
                {...getFieldValidation('tipoEmpaque')}
              >
                <Select 
                  placeholder="Seleccione tipo de empaque"
                  disabled={isReadOnly}
                  getPopupContainer={(trigger) => trigger.parentNode}
                  onChange={() => {
                    if(camposFaltantes.includes('tipoEmpaque')){
                      setCamposFaltantes(camposFaltantes.filter(c => c !== 'tipoEmpaque'))
                    }
                  }}
                  dropdownRender={(menu) => (
                    <div>
                      {menu}
                      {!isReadOnly && (
                        <div style={{ padding: '8px', borderTop: '1px solid #f0f0f0' }}>
                          <div style={{ display: 'flex', gap: '8px' }}>
                            <Input
                              placeholder="Nuevo empaque"
                              value={nuevoEmpaque}
                              onChange={(e) => setNuevoEmpaque(e.target.value)}
                              onPressEnter={handleAgregarEmpaque}
                              style={{ flex: 1 }}
                            />
                            <Button
                              type="primary"
                              icon={<PlusOutlined />}
                              onClick={handleAgregarEmpaque}
                              size="small"
                            >
                              Agregar
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                >
                  {tiposEmpaqueLocal.map((tipo) => (
                    <Select.Option key={tipo.id} value={tipo.id}>
                      {tipo.nombre}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item 
                label="Protección" 
                name="proteccion"
                {...getFieldValidation('proteccion')}
              >
                <Select 
                  mode="multiple"
                  placeholder="Seleccione protección"
                  disabled={isReadOnly}
                  getPopupContainer={(trigger) => trigger.parentNode}
                  onChange={() => {
                    if(camposFaltantes.includes('proteccion')){
                      setCamposFaltantes(camposFaltantes.filter(c => c !== 'proteccion'))
                    }
                  }}
                  dropdownRender={(menu) => (
                    <div>
                      {menu}
                      {!isReadOnly && (
                        <div style={{ padding: '8px', borderTop: '1px solid #f0f0f0' }}>
                          <div style={{ display: 'flex', gap: '8px' }}>
                            <Input
                              placeholder="Nuevo protección"
                              value={nuevoProteccion}
                              onChange={(e) => setNuevoProteccion(e.target.value)}
                              onPressEnter={handleAgregarProteccion}
                              style={{ flex: 1 }}
                            />
                            <Button
                              type="primary"
                              icon={<PlusOutlined />}
                              onClick={handleAgregarProteccion}
                              size="small"
                            >
                              Agregar
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                >
                  {tiposProteccionLocal.map((tipo) => (
                    <Select.Option key={tipo.id} value={tipo.id}>
                      {tipo.nombre}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item 
                label="Marca Fábrica" 
                name="marcaFabrica"
              >
                <Select 
                  allowClear
                  placeholder="Seleccione marca fábrica"
                  disabled={isReadOnly}
                  getPopupContainer={(trigger) => trigger.parentNode}
                  dropdownRender={(menu) => (
                    <div>
                      {menu}
                      {!isReadOnly && (
                        <div style={{ padding: '8px', borderTop: '1px solid #f0f0f0' }}>
                          <div style={{ display: 'flex', gap: '8px' }}>
                            <Input
                              placeholder="Nueva marca fábrica"
                              value={nuevoMarcaFabrica}
                              onChange={(e) => setNuevoMarcaFabrica(e.target.value)}
                              onPressEnter={handleAgregarMarcaFabrica}
                              style={{ flex: 1 }}
                            />
                            <Button
                              type="primary"
                              icon={<PlusOutlined />}
                              onClick={handleAgregarMarcaFabrica}
                              size="small"
                            >
                              Agregar
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                >
                  {marcasFabricaLocal.map((tipo) => (
                    <Select.Option key={tipo.id} value={tipo.id}>
                      {tipo.nombre}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item 
                label="Embalajes" 
                name="embalaje"
                {...getFieldValidation('embalaje')}
              >
                <Select 
                  placeholder="Seleccione embalaje"
                  disabled={isReadOnly}
                  getPopupContainer={(trigger) => trigger.parentNode}
                  onChange={() => {
                    if(camposFaltantes.includes('embalaje')){
                      setCamposFaltantes(camposFaltantes.filter(c => c !== 'embalaje'))
                    }
                  }}
                  dropdownRender={(menu) => (
                    <div>
                      {menu}
                      {!isReadOnly && (
                        <div style={{ padding: '8px', borderTop: '1px solid #f0f0f0' }}>
                          <div style={{ display: 'flex', gap: '8px' }}>
                            <Input
                              placeholder="Nuevo embalaje"
                              value={nuevoEmbalaje}
                              onChange={(e) => setNuevoEmbalaje(e.target.value)}
                              onPressEnter={handleAgregarEmbalaje}
                              style={{ flex: 1 }}
                            />
                            <Button
                              type="primary"
                              icon={<PlusOutlined />}
                              onClick={handleAgregarEmbalaje}
                              size="small"
                            >
                              Agregar
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                >
                  {tiposEmbalajeLocal.map((tipo) => (
                    <Select.Option key={tipo.id} value={tipo.id}>
                      {tipo.nombre}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
              <Form.Item
              label={
                <span>
                   Unidad de Empaque Recomendada{' '}
                  <span style={{ color: '#888', fontSize: '12px', fontWeight: 'normal' }}>
                    | {material.repuesto_unidad_empaque_actual}
                  </span>
                </span>
              }
                name="unidadEmpaqueRecomendada"
              >
                <Input
                  placeholder="Ingrese unidad de empaque"
                  disabled={isReadOnly}
                  onChange={(e) => {
                    form.setFieldsValue({
                      unidadEmpaqueRecomendada: e.target.value.toUpperCase(),
                    })
                  }}
                />
              </Form.Item>

              <Form.Item 
                label="Cambio de Etiqueta" 
                name="cambioEtiqueta"
                {...getFieldValidation('cambioEtiqueta')}
              >
                <Select 
                  placeholder="Seleccione"
                  disabled={isReadOnly}
                  getPopupContainer={(trigger) => trigger.parentNode}
                  onChange={() => {
                    if(camposFaltantes.includes('cambioEtiqueta')){
                      setCamposFaltantes(camposFaltantes.filter(c => c !== 'cambioEtiqueta'))
                    }
                  }}
                >
                  {cambioOpciones.map((opcion) => (
                    <Select.Option key={opcion} value={opcion}>
                      {opcion}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>

            {/* COLUMNA 3 */}
            <Col xs={24} sm={12} md={6} className="form-column-3">

              <Form.Item 
                label="Cambio de Empaque" 
                name="cambioEmpaque"
                {...getFieldValidation('cambioEmpaque')}
              >
                <Select 
                  placeholder="Seleccione"
                  disabled={isReadOnly}
                  getPopupContainer={(trigger) => trigger.parentNode}
                  onChange={() => {
                    if(camposFaltantes.includes('cambioEmpaque')){
                      setCamposFaltantes(camposFaltantes.filter(c => c !== 'cambioEmpaque'))
                    }
                  }}
                >
                  {cambioOpciones.map((opcion) => (
                    <Select.Option key={opcion} value={opcion}>
                      {opcion}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>

              <div className="tag-card">
                <div className="tag-card-header">
                  <span className="tag-card-title">Sustituto</span>
                  <Button
                    type="text"
                    icon={<PlusOutlined />}
                    onClick={() => handleAddTag('Homólogo')}
                    size="small"
                    disabled={isReadOnly}
                  />
                </div>
                <div className="tag-card-body">
                  <Space
                    direction="vertical"
                    size={4}
                    style={{ width: '100%' }}
                  >
                    {homologos.map((tag) => {
                      const esMatchHU = tag.origen === 'MOMENTOS_HU'
                      return (
                        <Tag
                          key={tag.repuesto_id}
                          icon={esMatchHU ? <ThunderboltFilled /> : undefined}
                          closable={!tag.isMaestro && !isReadOnly}
                          closeIcon={
                            !tag.isMaestro && !isReadOnly ? (
                              <CloseOutlined
                                onClick={(e) => {
                                  e.preventDefault()
                                  handleRemoveTag(tag.repuesto_id, 'Homólogo')
                                }}
                              />
                            ) : null
                          }
                          className={`compact-tag${
                            esMatchHU ? ' compact-tag--match' : ''
                          }`}
                          title={
                            esMatchHU
                              ? 'Agregado por match (Momentos HU)'
                              : undefined
                          }
                        >
                          {tag.codigo_material_sap} - {tag.descripcion}
                        </Tag>
                      )
                    })}
                  </Space>
                </div>
              </div>

              <div className="tag-card">
                <div className="tag-card-header">
                  <span className="tag-card-title">Reemplazante</span>
                  <Button
                    type="text"
                    icon={<PlusOutlined />}
                    onClick={() => handleAddTag('Reemplazante')}
                    size="small"
                    disabled={isReadOnly}
                  />
                </div>
                <div className="tag-card-body">
                  <Space
                    direction="vertical"
                    size={4}
                    style={{ width: '100%' }}
                  >
                    {reemplazantes.map((tag) => {
                      const esMatchHU = tag.origen === 'MOMENTOS_HU'
                      return (
                        <Tag
                          key={tag.repuesto_id}
                          icon={esMatchHU ? <ThunderboltFilled /> : undefined}
                          closable={!tag.isMaestro && !isReadOnly}
                          closeIcon={
                            !tag.isMaestro && !isReadOnly ? (
                              <CloseOutlined
                                onClick={(e) => {
                                  e.preventDefault()
                                  handleRemoveTag(tag.repuesto_id, 'Reemplazante')
                                }}
                              />
                            ) : null
                          }
                          className={`compact-tag${
                            esMatchHU ? ' compact-tag--match' : ''
                          }`}
                          title={
                            esMatchHU
                              ? 'Agregado por match (Momentos HU)'
                              : undefined
                          }
                        >
                          {tag.codigo_material_sap} - {tag.descripcion}
                        </Tag>
                      )
                    })}
                  </Space>
                </div>
              </div>

              <div className="tag-card">
                <div className="tag-card-header">
                  <span className="tag-card-title">
                    Modelos de Aplicación Principal
                  </span>
                  <Button
                    type="text"
                    icon={<PlusOutlined />}
                    onClick={() =>
                      handleAddTagModelMoto('Modelo Aplicación Principal')
                    }
                    size="small"
                    disabled={isReadOnly}
                  />
                </div>
                <div className="tag-card-body">
                  <Space
                    direction="vertical"
                    size={4}
                    style={{ width: '100%' }}
                  >
                    {modelosAplicacionPrincipal.map((tag) => (
                      <Tag
                        key={tag.repuesto_id}
                        closable={!tag.isMaestro && !isReadOnly}
                        closeIcon={
                          !tag.isMaestro && !isReadOnly ? (
                            <CloseOutlined
                              onClick={(e) => {
                                e.preventDefault()
                                handleRemoveTag(
                                  tag.id,
                                  'Modelo Aplicación Principal',
                                )
                              }}
                            />
                          ) : null
                        }
                        className="compact-tag"
                      >
                        {tag.id} - {tag.nombre}
                      </Tag>
                    ))}
                  </Space>
                </div>
              </div>
              <div className="tag-card">
                <div className="tag-card-header">
                  <span className="tag-card-title">Modelos de Aplicación</span>
                  <Button
                    type="text"
                    icon={<PlusOutlined />}
                    onClick={() => handleAddTagModelMoto('Modelo Aplicación')}
                    size="small"
                    disabled={isReadOnly}
                  />
                </div>
                <div className="tag-card-body">
                  <Space
                    direction="vertical"
                    size={4}
                    style={{ width: '100%' }}
                  >
                    {modelosAplicacion.map((tag) => (
                      <Tag
                        key={tag.id}
                        closable={!isReadOnly}
                        closeIcon={
                          !isReadOnly ? (
                            <CloseOutlined
                              onClick={(e) => {
                                e.preventDefault()
                                handleRemoveTag(tag.id, 'Modelo Aplicación')
                              }}
                            />
                          ) : null
                        }
                        className="compact-tag"
                      >
                        {tag.id} - {tag.nombre}
                      </Tag>
                    ))}
                  </Space>
                </div>
              </div>
              <Form.Item 
                label="Observación" 
                name="observacion"
                {...getFieldValidation('observacion')}
              >
                <TextArea
                  rows={3}
                  placeholder="Ingrese observaciones"
                  style={{ resize: 'none' }}
                  disabled={isReadOnly}
                  onChange={(e) => {
                    form.setFieldsValue({
                      observacion: e.target.value.toUpperCase(),
                    })
                    if(camposFaltantes.includes('observacion')){
                      setCamposFaltantes(camposFaltantes.filter(c => c !== 'observacion'))
                    }
                  }}
                />
              </Form.Item>
              <CatalogUpdateCard
                image={catalogUpdateImage}
                isReadOnly={isReadOnly}
                onUpload={handleUploadCatalogUpdateImage}
                onDelete={handleDeleteCatalogUpdateImage}
                onDownload={handleDownloadImage}
              />
            </Col>

            {/* COLUMNA 4: Material Info, Fotos */}
            <Col xs={24} sm={12} md={6} className="form-column-4">
              <MaterialInfoCard
                material={material}
                idUser={idUser}
                isAuditor={isAuditor}
                isReadOnly={isReadOnly}
                onClose={onClose}
                onSave={() => handleSubmitSave(form.getFieldsValue())}
                onLiberar={liberarMaterial}
              />

              {tieneFotosMaterial && (
                <BuscarReemplazantesSection
                  codigoMaterial={material.repuesto_codigo_material_sap}
                  material={material}
                  imagenesMaterial={images}
                  categorias={categorias}
                  subCategorias={subCategorias}
                  segmentos={segmentos}
                  subSegmentos={subSegmentos}
                  marcas={marcas}
                  form={form}
                  reemplazantes={reemplazantes}
                  sustitutos={homologos}
                  isReadOnly={isReadOnly}
                  onMatchReemplazante={handleMatchReemplazanteFromBusqueda}
                  onUnmatchReemplazante={handleUnmatchReemplazanteFromBusqueda}
                  onMatchSustituto={handleMatchSustitutoFromBusqueda}
                  onUnmatchSustituto={handleUnmatchSustitutoFromBusqueda}
                  disabled={
                    isReadOnly || buscarReemplazantesCooldown > 0
                  }
                  cooldownSeconds={buscarReemplazantesCooldown}
                />
              )}

              <PhotosCard
                images={images}
                isReadOnly={isReadOnly}
                basculaConectada={basculaConectada}
                todasLasCotasGuardadas={todasLasCotasGuardadas}
                imagenFavorita={imagenFavorita}
                onUpload={handleUploadReferenceImage}
                onDelete={handleDeleteImage}
                onDownload={handleDownloadImage}
                onTomarFoto={() => handleTomarFoto('material')}
                // onTomarFoto={handleTomarFotoMock}
                onVerCotasGuardadas={handleVerCotasGuardadas}
                onSetImagenFavorita={(nombre) => setImagenFavorita(nombre)}
              />

              <ImagenesEmpaqueCard
                images={imagenesEmpaque}
                isReadOnly={isReadOnly}
                onUpload={handleUploadImagenEmpaque}
                onDelete={handleDeleteImagenEmpaque}
                basculaConectada={basculaConectada}
                onTomarFoto={() => handleTomarFoto('empaque')}
              />

              <Form.Item label="Comentario Auditor" name="comentarioAuditor">
                <TextArea
                  rows={3}
                  disabled={!isAuditor || isReadOnly}
                  placeholder="Ingrese comentario auditor"
                  style={{ resize: 'none' }}
                  onChange={(e) => {
                    form.setFieldsValue({
                      comentarioAuditor: e.target.value.toUpperCase(),
                    })
                  }}
                />
              </Form.Item>
              {isAuditor && (
                <Button
                  type="primary"
                  htmlType="submit"
                  block
                  disabled={isReadOnly}
                  style={{
                    backgroundColor: '#2563EB',
                    borderRadius: '8px',
                    height: '40px',
                    fontWeight: 500,
                    maxWidth: '300px',
                    marginTop: '20px',
                  }}
                >
                  Auditar 
                </Button>
              )}
              {!isAuditor &&
                material.estado !== 'REVISADO' &&
                !isAuditor &&
                material.estado !== 'AUDITADO' && (
                  <Button
                    type="primary"
                    htmlType="submit"
                    block
                    disabled={
                      isReadOnly ||
                      (material.estado === 'REVISADO' && !isAuditor) ||
                      material.estado === ''
                    }
                    style={{
                      backgroundColor: '#2563EB',
                      borderRadius: '8px',
                      height: '40px',
                      fontWeight: 500,
                      maxWidth: '300px',
                      marginTop: '20px',
                    }}
                  >
                    Revisar (axiomer)
                  </Button>
                )}
              {isAuditor && material.estado === 'REVISADO' && (
                <Button
                  type="primary"
                  block
                  disabled={
                    isReadOnly ||
                    (material.estado === 'REVISADO' && !isAuditor) ||
                    material.estado === ''
                  }
                  style={{
                    backgroundColor: 'green',
                    borderRadius: '8px',
                    height: '40px',
                    fontWeight: 500,
                    maxWidth: '300px',
                    marginTop: '20px',
                  }}
                  onClick={() =>
                    handleSubmitSave(form.getFieldsValue(), false, true)
                  }
                >
                  Reasginar
                </Button>
              )}
            </Col>

            {/* FILA FINAL: Fotos del Material + Botón Revisar */}
            <Col xs={24} sm={12} md={12} />
            <Col xs={24} sm={12} md={12} />
          </Row>
        </Form>

        {/* Modal para mostrar la imagen capturada y guardar en catálogo (paso 2: quitar fondo y colores) */}
        <ModalMedidasCanvas
          visible={modalImagenVisible}
          onClose={() => {
            if (imagenFirmadaUrl && imagenFirmadaUrl.startsWith('blob:')) {
              URL.revokeObjectURL(imagenFirmadaUrl)
            }
            setModalImagenVisible(false)
            setImagenFirmadaUrl(null)
            setImagenFirmadaBlob(null)
            setGsUriFirmado(null)
            setGsurlRecortado(null)
          }}
          onGuardarFoto={handleGuardarFotoMaterial}
          imagenFirmadaUrl={imagenFirmadaUrl}
          imagenFirmadaBlob={imagenFirmadaBlob}
          onError={(msg) => toast.error(msg)}
        />

        {/* Modal para visualizar cotas guardadas (solo lectura) */}
        <ModalCotasGuardadas
          visible={modalCotasVisible}
          onClose={() => {
            setModalCotasVisible(false)
            setImagenCotasUrl(null)
            coordenadasCotasRef.current = []
            setCoordenadasCotas([])
            setMedidasCotas([])
          }}
          imagenCotasUrl={imagenCotasUrl}
          todasLasCotasGuardadas={todasLasCotasGuardadas}
          cotaSeleccionadaId={cotaSeleccionadaId}
          onChangeMedidaSeleccionada={handleCambiarMedidaSeleccionada}
          onBorrarMedida={handleEliminarMedidas}
          coordenadasCotas={coordenadasCotas}
          medidasCotas={medidasCotas}
          canvasCotasRef={canvasCotasRef}
          imageCotasRef={imageCotasRef}
          onCanvasClick={handleClickCanvasCotas}
          onCanvasMouseMove={handleMouseMoveCanvasCotas}
        />

        {/* Modal de Edición y Recorte de Imagen (paso 1 cuando viene de foto; o edición directa) */}
        <ModalEdicionImagen
          visible={modalEdicionVisible}
          onCancel={handleCancelarEdicion}
          onLimpiarSeleccion={handleLimpiarSeleccion}
          onAplicarRecorte={handleAplicarRecorte}
          onContinuar={handleContinuarDesdeEdicion}
          mostrarBotonContinuar={flujoFotoPrimeroEdicion}
          imagenParaEditar={imagenParaEditar}
          rotacionAngulo={rotacionAngulo}
          selectionStart={selectionStart}
          selectionEnd={selectionEnd}
          cropImageRef={cropImageRef}
          onMouseDownCrop={handleMouseDownCrop}
          onMouseMoveCrop={handleMouseMoveCrop}
          onMouseUpCrop={handleMouseUpCrop}
          onRotarIzquierda={handleRotarIzquierda}
          onRotarDerecha={handleRotarDerecha}
        />

        {/* Modal de Recorte de Imagen */}
        <ModalRecorte
          visible={modalRecorteVisible}
          onCancel={() => {
            setModalRecorteVisible(false)
            setImagenOriginalParaRecorte(null)
          }}
          imagenUrl={imagenOriginalParaRecorte}
          onConfirmarRecorte={handleConfirmarRecorte}
          recortando={recortando}
        />

        <ModalImagenCotizacion
          open={modalCotizacionOpen}
          onClose={() => setModalCotizacionOpen(false)}
          fotos={images}
          fotosEmpaque={imagenesEmpaque}
          imagenFavorita={imagenFavorita}
          especificaciones={jsonMedidasFromMaterial?.especificaciones}
          imagenesPlantilla={imagenesPlantillaCotizacion}
          cargandoPlantilla={cargandoPlantillaCotizacion}
          nombreArchivo={material?.repuesto_codigo_material_sap || 'material'}
          imagenGuardada={imagenCotizacionGuardada}
          puedeCrearNueva={plantillaMedidasGuardada && hayFotosCotizacion}
          motivoNoCrear={
            !plantillaMedidasGuardada
              ? 'El material no tiene medidas guardadas en la plantilla'
              : 'Sube o toma al menos una foto del material o del empaque'
          }
          onGuardar={handleGuardarImagenCotizacion}
        />

        {/* Modal Ver medidas (json_medidas) */}
        <Modal
          width="100%"
          title="Ver medidas"
          open={modalVerMedidasOpen}
          maskClosable={false}
          closable={false}
          onCancel={() => {
            setModalVerMedidasOpen(false)
            setJsonMedidasEnEdicion([])
            setOpcionesMedidas([])
            setImagenVerMedidasUrl(null)
            setImagenesVerMedidasUrls([])
            setImagenesVerMedidasNombres([])
            setVerMedidasCarouselIndex(0)
            setActiveMeasureTarget(null)
            setEditandoMedidasGuardadas(false)
          }}
          footer={[
            <Button
              key="cancelar"
              onClick={() => {
                setModalVerMedidasOpen(false)
                setJsonMedidasEnEdicion([])
                setOpcionesMedidas([])
                setImagenVerMedidasUrl(null)
                setImagenesVerMedidasUrls([])
                setImagenesVerMedidasNombres([])
                setVerMedidasCarouselIndex(0)
                setActiveMeasureTarget(null)
                setEditandoMedidasGuardadas(false)
              }}
            >
            Cancelar
            </Button>,
            medidasBloqueadas && !isReadOnly && (
              <Button
                key="editar"
                icon={<EditOutlined />}
                onClick={() => {
                  setMedidasBloqueadas(false)
                  setEditandoMedidasGuardadas(true)
                }}
              >
                Editar medidas
              </Button>
            ),
            <Button
              key="guardar"
              type="primary"
              disabled={medidasBloqueadas || isReadOnly}
              onClick={() => {
                Modal.confirm({
                  title: '¿Seguro que quieres guardar estas medidas?',
                  content: 'Después no podrás editarlas.',
                  okText: 'Sí, guardar',
                  cancelText: 'Cancelar',
                  onOk: async () => {
                    const conValores = jsonMedidasEnEdicion.map(({ valor, medida, nombre, definicion_cotas: defCotas, tipo_campo: tipoCampo, opciones: opcs }) => ({
                      valor: String(valor ?? ''),
                      medida: medida ?? '',
                      nombre: defCotas ?? nombre ?? '',
                      definicion_cotas: defCotas ?? nombre ?? '',
                      tipo_campo: tipoCampo ?? 'Num',
                      opciones: opcs ?? '',
                    }))
                    if (Array.isArray(jsonMedidasEditable)) {
                      setJsonMedidasEditable(conValores)
                    } else if (jsonMedidasEditable?.especificaciones) {
                      setJsonMedidasEditable({
                        ...jsonMedidasEditable,
                        especificaciones: conValores.map(m => ({
                          medida: m.medida,
                          descripcion: m.definicion_cotas ?? m.nombre,
                          definicion_cotas: m.definicion_cotas ?? m.nombre,
                          tipo_campo: m.tipo_campo ?? 'Num',
                          opciones: m.opciones ?? '',
                          valor: m.valor,
                        })),
                      })
                    }
                    setMedidasBloqueadas(true)
                    setEditandoMedidasGuardadas(false)
                    setModalVerMedidasOpen(false)
                    setJsonMedidasEnEdicion([])
                    setOpcionesMedidas([])
                    setImagenVerMedidasUrl(null)
                    setImagenesVerMedidasUrls([])
                    setImagenesVerMedidasNombres([])
                    setVerMedidasCarouselIndex(0)
                    setActiveMeasureTarget(null)

                    await handleSubmitSave(form.getFieldsValue(), false, undefined, conValores)
                  },
                })
              }}
            >
              Guardar
            </Button>,
          ]}
        >
          {opcionesMedidas.length > 0 && (
            <Row gutter={20} style={{ marginBottom: 16 }}>
              <Col span={24}>
                <span style={{ marginRight: 8, fontWeight: 500 }}>Descripción:</span>
                <Select
                  placeholder="Seleccione una descripción"
                  style={{ minWidth: 280 }}
                  value={!Array.isArray(jsonMedidasEditable) && jsonMedidasEditable?.definicion_cotas && opcionesMedidas.some(o => o.descripcion === jsonMedidasEditable.definicion_cotas) ? jsonMedidasEditable.definicion_cotas : undefined}
                  onChange={async (nuevaDescripcion) => {
                    const values = form.getFieldsValue()
                    const categoriaId = values.categoria || material?.revision_categoria_sugerida_id
                    const subcategoriaId = values.subcategoria || material?.revision_subcategoria_sugerida_id
                    if (!categoriaId || !subcategoriaId) {
                      toast.error('No se puede cambiar la descripción sin categoría y subcategoría')
                      return
                    }
                    setLoadingPlantillaAlCambiarDescripcion(true)
                    try {
                      const jsonMedidas = await handlePlantillaMedidas(categoriaId, subcategoriaId, nuevaDescripcion)
                      if (!jsonMedidas || (Array.isArray(jsonMedidas) && jsonMedidas.length === 0)) {
                        toast.error('No se encontraron medidas para la descripción seleccionada')
                        return
                      }
                      const list = (jsonMedidas.especificaciones ?? []).map(e => ({
                        valor: e.tipo_campo === 'ARR' ? (e.valor ?? '') : e.valor,
                        medida: e.medida ?? '',
                        nombre: e.definicion_cotas ?? e.descripcion ?? e.nombre ?? '',
                        definicion_cotas: e.definicion_cotas ?? e.descripcion ?? e.nombre ?? '',
                        tipo_campo: e.tipo_campo ?? 'Num',
                        opciones: e.opciones ?? '',
                      }))
                      setJsonMedidasEnEdicion(
                        list.map((m, i) => ({
                          ...m,
                          _key: `medida-${i}-${Date.now()}`,
                        }))
                      )
                      setImagenVerMedidasUrl(null)
                      const procs = jsonMedidas?.imagenes_procesadas
                      if (Array.isArray(procs) && procs.length > 0) {
                        const urls = procs.map(p => p.signed_url).filter(Boolean)
                        const nombres = procs.map(p => (p.gs_url && typeof p.gs_url === 'string' ? p.gs_url.replace(/^.*\//, '') : '')).filter(Boolean)
                        setImagenesVerMedidasUrls(urls)
                        setImagenesVerMedidasNombres(nombres.length === urls.length ? nombres : urls.map(() => ''))
                      } else {
                        const gsUrl = jsonMedidas?.gs_url ?? null
                        if (gsUrl) {
                          const res = await postFirmarRevision(gsUrl)
                          setImagenesVerMedidasUrls(res?.url ? [res.url] : [])
                          setImagenesVerMedidasNombres([gsUrl.replace(/^.*\//, '')])
                        } else {
                          setImagenesVerMedidasUrls([])
                          setImagenesVerMedidasNombres([])
                        }
                      }
                      setVerMedidasCarouselIndex(0)
                    } finally {
                      setLoadingPlantillaAlCambiarDescripcion(false)
                    }
                  }}
                  loading={loadingPlantillaAlCambiarDescripcion}
                  disabled={isReadOnly || medidasBloqueadas || editandoMedidasGuardadas || loadingPlantillaAlCambiarDescripcion}
                  getPopupContainer={(trigger) => trigger.parentNode}
                >
                  {(opcionesMedidas.length > 0
                    ? opcionesMedidas
                    : jsonMedidasEditable?.definicion_cotas
                      ? [{ descripcion: jsonMedidasEditable.definicion_cotas }]
                      : []
                  ).map((opcion) => (
                    <Select.Option key={opcion.descripcion} value={opcion.descripcion}>
                      {opcion.descripcion}
                    </Select.Option>
                  ))}
                </Select>
              </Col>
            </Row>
          )}
          {opcionesMedidas.length > 0 && jsonMedidasEnEdicion.length === 0 && (
            <p style={{ color: '#888', marginTop: 8, marginBottom: 0 }}>Seleccione una descripción para cargar las medidas y la imagen de referencia.</p>
          )}
          <Row gutter={20} style={{ marginTop: 8 }} align="top">
            {(imagenesVerMedidasUrls.length > 0 || imagenVerMedidasUrl) && (
              <Col xs={24} sm={24} md={20} lg={16}>
                <div className="ver-medidas-imagenes">
                  {imagenesVerMedidasUrls.length > 1 ? (
                    <>
                      <div className="ver-medidas-imagenes__header">
                        <span className="ver-medidas-imagenes__counter">
                          Imagen {verMedidasCarouselIndex + 1} de {imagenesVerMedidasUrls.length}
                          {imagenesVerMedidasNombres[verMedidasCarouselIndex] ? ` ${imagenesVerMedidasNombres[verMedidasCarouselIndex]}` : ''}
                        </span>
                        <div className="ver-medidas-imagenes__nav">
                          <Button
                            type="text"
                            icon={<LeftOutlined />}
                            onClick={() => carouselVerMedidasRef.current?.prev()}
                            disabled={verMedidasCarouselIndex === 0}
                            className="ver-medidas-imagenes__nav-btn"
                            aria-label="Imagen anterior"
                          />
                          <Button
                            type="text"
                            icon={<RightOutlined />}
                            onClick={() => carouselVerMedidasRef.current?.next()}
                            disabled={verMedidasCarouselIndex === imagenesVerMedidasUrls.length - 1}
                            className="ver-medidas-imagenes__nav-btn"
                            aria-label="Siguiente imagen"
                          />
                        </div>
                      </div>
                      <Carousel
                        ref={carouselVerMedidasRef}
                        dots={{ className: 'ver-medidas-imagenes__dots' }}
                        arrows
                        afterChange={(idx) => setVerMedidasCarouselIndex(idx)}
                        className="ver-medidas-imagenes__carousel"
                      >
                        {imagenesVerMedidasUrls.map((url, idx) => (
                          <div key={url} className="ver-medidas-imagenes__slide">
                            <img
                              src={url}
                              alt={`Referencia ${idx + 1}`}
                              className="ver-medidas-imagenes__img"
                            />
                          </div>
                        ))}
                      </Carousel>
                    </>
                  ) : (
                    <div className="ver-medidas-imagenes__single">
                      <img
                        src={imagenesVerMedidasUrls[0] || imagenVerMedidasUrl}
                        alt="Referencia medidas"
                        className="ver-medidas-imagenes__img"
                      />
                    </div>
                  )}
                </div>
              </Col>
            )}
            <Col xs={24} sm={24} md={imagenesVerMedidasUrls.length > 0 || imagenVerMedidasUrl ? 12 : 24} lg={8}>
              <div
                style={{
                  maxHeight: jsonMedidasEnEdicion.length > 6 ? 380 : 'none',
                  overflowY: jsonMedidasEnEdicion.length > 6 ? 'auto' : 'visible',
                  paddingRight: jsonMedidasEnEdicion.length > 6 ? 8 : 0,
                }}
              >
                <Form layout="vertical" style={{ marginBottom: 0 }} disabled={isReadOnly}>
                  {jsonMedidasEnEdicion.map((item, index) => {
                    const isMedidaActiva = activeMeasureTarget?.type === 'modal' && activeMeasureTarget.index === index
                    const indicadorConectado = basculaConectada && isMedidaActiva
                    const esTexto = item.tipo_campo === 'Text'
                    const esArr = item.tipo_campo === 'ARR'
                    const opcionesArr = (item.opciones && typeof item.opciones === 'string')
                      ? item.opciones.split('|').map(s => s.trim()).filter(Boolean)
                      : []
                    const labelTexto = item.definicion_cotas ?? `${item.medida ?? ''}: ${item.nombre ?? ''}`
                    return (
                  <Form.Item
                    key={item._key ?? `medida-${index}`}
                    label={
                      <span style={{ color: 'rgba(0,0,0,0.88)', fontSize: 12, fontWeight: 400, display: 'block', marginBottom: 0 }}>
                        <b style={{ fontWeight: 600, color: 'red' }}>{item.medida ?? ''}: </b> {labelTexto}
                      </span>
                    }
                    style={{ marginBottom: 15 }}
                    colon={false}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    {esArr ? (
                      <Select
                        size="small"
                        placeholder="Seleccione"
                        value={item.valor != null && item.valor !== '' ? String(item.valor) : undefined}
                        onChange={(v) => {
                          setJsonMedidasEnEdicion(prev =>
                            prev.map((m, i) => (i === index ? { ...m, valor: v ?? '' } : m))
                          )
                        }}
                        disabled={isReadOnly || medidasBloqueadas}
                        style={{ minWidth: 120, flex: 1, maxWidth: 280 }}
                        getPopupContainer={(trigger) => trigger.parentNode}
                      >
                        {opcionesArr.map((opt) => (
                          <Select.Option key={opt} value={opt}>
                            {opt}
                          </Select.Option>
                        ))}
                      </Select>
                    ) : (
                      <>
                    <Input
                      type="text"
                      inputMode={esTexto ? 'text' : 'decimal'}
                      size="small"
                      value={item.valor ?? ''}
                      onFocus={() => setActiveMeasureTarget({ type: 'modal', index })}
                      onChange={(e) => {
                        const v = e.target.value
                        if (esTexto) {
                          setJsonMedidasEnEdicion(prev =>
                            prev.map((m, i) => (i === index ? { ...m, valor: v } : m))
                          )
                        } else {
                          const sanitized = v.replace(',', '.')
                          if (/^-?\d*\.?\d*$/.test(sanitized) || sanitized === '') {
                            setJsonMedidasEnEdicion(prev =>
                              prev.map((m, i) => (i === index ? { ...m, valor: sanitized } : m))
                            )
                          }
                        }
                      }}
                      placeholder={esTexto ? 'Observaciones' : '0'}
                      disabled={isReadOnly || medidasBloqueadas}
                      style={{
                        width: esTexto ? '100%' : 96,
                        maxWidth: esTexto ? '100%' : 96,
                        textAlign: esTexto ? 'left' : 'right',
                        borderRadius: 6,
                      }}
                    />
                    {!esTexto && (
                      <>
                        <span
                          style={{
                            display: 'inline-block',
                            width: 12,
                            height: 12,
                            borderRadius: '50%',
                            backgroundColor: indicadorConectado ? '#52c41a' : '#ff4d4f',
                            flexShrink: 0,
                          }}
                          title={indicadorConectado ? 'Este campo recibe datos del socket' : basculaConectada ? 'Haz clic en el campo para recibir la medida' : 'Socket no conectado'}
                        />
                        <p style={{ marginLeft: 0, marginBottom: 0, fontSize: 12, fontWeight: 400 }}>mm</p>
                      </>
                    )}
                      </>
                    )}
                    </div>
                  </Form.Item>
                    )
                  })}
                </Form>
              </div>
            </Col>
          </Row>
        </Modal>
      </div>
    </div>
  )
}

export default AxiomaFormContainer
